import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    IconButton,
    TextField,
    MenuItem,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import GroupIcon from '@material-ui/icons/Group';
import BusinessIcon from '@material-ui/icons/Business';
import useStyles from '../../../assets/styles/ManageClassesStyles';
import AddClassStudentsDialog from './components/AddClassStudentsDialog';
import EditClassDialog from './components/EditClassDialog';
import MyClassesGuide from './components/MyClassesGuide';
import ManageLearnCreatorTeachersDialog from './components/ManageLearnCreatorTeachersDialog';
import AssignLicensesDialog from '../components/AssignLicensesDialog.comp';
import LinkTypePickerDialog, { LINK_TYPE } from '../components/LinkTypePickerDialog.comp';
import ManageClassesLinkProgramDialog from '../components/ManageClassesLinkProgramDialog.comp';
import ManageClassesLinkAssessmentDialog from '../components/ManageClassesLinkAssessmentDialog.comp';
import LoaderWithBackDrop from '../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import { useAuth } from '../../../context/auth';
import { isTeacher, canActOnSection, membershipSectionCodes } from '../../../utils/capabilities';
import { getUserDetailsService, updateUserDetailsService } from '../../../services/auth.service';
import { listTeacherService } from '../../../services/teacher.service';
import { isClientOnboardingUser, mergeOnboardingRefreshIntoAuthUser } from '../DQProV2/constants/onboardingSteps.config';
import {
    genSectionCode,
    listOrgSectionsService,
    createOrgClassSectionsService,
    updateOrgClassSectionService,
    assignSectionLicensesService,
    getSectionLicensesService,
    getSectionLinkedModulesService,
    getOrgModulesService,
    getOrgDetailsService,
    linkSectionAssessmentService,
    learnerSpacePreviewService,
    publishLearnerSpacesService,
    createLicenceRequestService,
    listClientsService,
} from '../../../services/client.service';
import { getInsiderAssessmentsService } from '../../../services/assessments.service';
import {
    getInsiderProgramsService,
    linkSectionLearningProgramService,
    getLinkedLearningProgramService,
} from '../../../services/learning_programs.service';
import {
    analyticsLicensesInfoService,
    analyticsPostUsersCsvService,
    analyticsPostUsersCsvAsyncService,
    analyticsCsvUploadJobStatusService,
    analyticsGetUsersCsvService,
} from '../../../services/analyticsLicenses.service';
import {
    buildStudentUploadTemplateWorkbook,
    pickDefaultStudentUserGroup,
    downloadExcelJsWorkbook,
    parseStudentLicenseWorkbookToPostData,
    licenseRowsOfClass,
    buildInsiderLicensePostDataFromClassTabs,
    splitClassTabsForManageClassesSave,
    validateManualEnrollTabsForCsvPost,
    ORG_TYPE_SCHOOL,
    buildExistingUsersWorkbook,
    getExistingUsersExportKeys,
    buildClassImportExportBlob,
    importTemplateKindOfFile,
} from '../../../utils/analyticsLicensesExcel';
import {
    TEMPLATE_SCHOOL_CLASS, TEMPLATE_NETWORK_CLASS, downloadImportTemplate, saveTemplateBlob,
    fillImportTemplate, IMPORT_TEMPLATES,
} from '../../../utils/importTemplates';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES, USER_GROUPS, PRODUCT_KEY, FEATURE_KEY, LEARNING_PROGRAM_STATUS, DQ_PRO_PLAN_KEY } from '../../../utils/constants';
import AccountLifecycleBadge, { isAccountDraft, isAccountPublished } from '../components/AccountLifecycleBadge.comp';
import {
    LearnerSpaceScopeDialog,
    ConfirmPublishLearnerSpacesDialog,
    InsufficientLicencesDialog,
    LearningSpacePublishedDialog,
} from '../components/PublishLearnerSpacesDialogs.comp';
import { canCreateProduct, canLinkProduct, isProductEnabled, isFeatureEnabled, solutionPlanKey, isLinkingFreeOnPlan } from '../../../utils/productAccess';
// getCreditBalanceService no longer used here — My Classes shows no credits
// card for a school or network (Ian-SW-29Sep-6, Ian-SNW-29Sep-15).
import { validateClassListForCreateApi } from '../../../utils/classCreateValidation';
import { silentAssignSectionPool } from '../../../services/sections/sections.service';
import { extractOrgSectionsTableData, isOrgSectionRow } from '../../../utils/orgSectionsNormalize';
import { getCountries, useGeo } from '../../../utils/geo';

/** Section license stats on section payloads (licenses.total / licenses.used only). */
const readSectionLicenseStats = (s) => {
    let lic = s && typeof s.licenses === 'object' && s.licenses !== null && !Array.isArray(s.licenses)
        ? s.licenses
        : null;
    if (!lic && s?.additional_details?.licenses && typeof s.additional_details.licenses === 'object') {
        lic = s.additional_details.licenses;
    }
    const total = lic != null && lic.total != null ? Number(lic.total) : 0;
    const used  = lic != null && lic.used  != null ? Number(lic.used)  : 0;
    return {
        total: Number.isFinite(total) ? total : 0,
        used:  Number.isFinite(used)  ? used  : 0,
    };
};

/**
 * Enrolled students for this class.
 * Reads student_count from the API response (live DB count returned by getSectionLicenses).
 * Falls back to 0 when the API data is not yet loaded.
 */
const readSectionStudentCount = (sectionLicensesFromApi) => {
    if (sectionLicensesFromApi && sectionLicensesFromApi.student_count != null) {
        const n = Number(sectionLicensesFromApi.student_count);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    }
    return 0;
};

/** Unwrap `GET /dqin/client/org/:org_code` (E2EE-safe). */
const unwrapOrgFromDetailsRes = (res) => {
    const d = res?.data;
    if (!d || typeof d !== 'object') return null;
    if (d.org && typeof d.org === 'object') return d.org;
    if (d.data?.org && typeof d.data.org === 'object') return d.data.org;
    return null;
};

/** Org pool snapshot — `org.licenses_dq_assess`, handover #8 `org_licenses`, or CSV `dqp_info`. */
const buildOrgLicenseClient = (licenseInfo) => {
    if (!licenseInfo || typeof licenseInfo !== 'object') return null;
    const used = Number(licenseInfo.used) || 0;
    const allocatedRaw = licenseInfo.allocated;
    const allocated = allocatedRaw != null && allocatedRaw !== '' ? Number(allocatedRaw) : NaN;
    return {
        licenses_used: used,
        licenses_allocated: Number.isFinite(allocated) ? allocated : used,
        licenses_total: Number(licenseInfo.total) || 0,
        users: Number(licenseInfo.no_of_users) || 0,
    };
};

/** Sync org pool header from CSV upload response before reload. */
const patchClientLicensesFromDqpInfo = (setPrimaryClient, dqpInfo) => {
    if (!dqpInfo || typeof dqpInfo !== 'object') return;
    const used = Number(dqpInfo.used);
    const total = Number(dqpInfo.total);
    const allocated = Number(dqpInfo.allocated);
    setPrimaryClient((prev) => {
        const base = prev || buildOrgLicenseClient(dqpInfo) || { licenses_used: 0, licenses_allocated: 0, licenses_total: 0, users: 0 };
        return {
            ...base,
            ...(Number.isFinite(used) ? { licenses_used: used } : {}),
            ...(Number.isFinite(allocated) ? { licenses_allocated: allocated } : {}),
            ...(Number.isFinite(total) ? { licenses_total: total } : {}),
        };
    });
};


// Row threshold for the CSV upload dispatch mode. Files at or below this size
// use the original synchronous request (fast, inline row errors). Larger files
// go to the async job endpoint: the server responds with a job_id immediately
// and processes rows in the background while this page polls for progress, so
// neither the request (gateway timeout) nor the user (blocked dialog) has to
// wait out the whole run. Re-uploading a file is always safe: the backend
// UPDATES existing students (matched by email/username), it never duplicates.
const UPLOAD_BATCH_SIZE = 100;

// A processing upload job whose doc hasn't been touched for this long is
// considered stalled (e.g. API restart mid-run) — mirrors the server's guard.
const UPLOAD_JOB_STALE_MS = 5 * 60 * 1000;

// Uniform outline style for the row actions (Edit Details / Manage Students /
// Link Programs / Class Dashboard), replacing the old per-action color scheme
// (orange/blue/magenta/teal) — matches the "Download Excel" outline treatment.
/*
 * The My Classes toolbar (Ian-SNW, SW-12Oct-1, slide 55): two outlined and two
 * filled buttons, at the height of the My Schools header buttons.
 */
const toolbarBtnBase = {
    // The mockup's: 36px high, 13px bold, 18px side padding.
    borderRadius: '18px', height: 36, padding: '0 18px', textTransform: 'none', fontWeight: 700,
    fontSize: '13px', whiteSpace: 'nowrap', boxShadow: 'none', minWidth: 0, boxSizing: 'border-box', flexShrink: 0,
};
const toolbarOutlineSx = {
    ...toolbarBtnBase,
    border: '1.5px solid #FF6900', color: '#FF6900', backgroundColor: 'var(--brand-surface, #fff)',
    '&:hover': { backgroundColor: 'rgba(255,105,0,0.08)', borderColor: '#FF6900' },
    '&.Mui-disabled': { color: 'rgba(255,105,0,0.4)', borderColor: 'rgba(255,105,0,0.3)' },
};
const toolbarFilledSx = {
    ...toolbarBtnBase,
    // The two filled buttons share one width.
    border: 'none', width: 180, color: '#fff', backgroundColor: '#FF6900',
    '&:hover': { backgroundColor: '#e55e00' },
    '&.Mui-disabled': { color: '#fff', backgroundColor: '#FF6900', opacity: 0.45 },
};

const outlineActionBtnSx = {
    color: '#FF6900',
    borderColor: '#FF6900',
    borderRadius: '50px',
    padding: '7px 18px',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '13px',
    minWidth: '70px',
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: 'rgba(255,105,0,0.08)', borderColor: '#FF6900' },
    '&.Mui-disabled': { color: 'rgba(255,105,0,0.4)', borderColor: 'rgba(255,105,0,0.3)' },
};

const formatLicenseCell = (classUsed, classTotal) => {
    const cu = Number(classUsed) || 0;
    const ct = Number(classTotal) || 0;
    if (ct > 0) {
        const avail = Math.max(0, ct - cu);
        return `${cu} used / ${avail} available`;
    }
    if (cu > 0) {
        return `${cu} used / no pool set`;
    }
    return '0 used / 0 available';
};

/** Merge section/list row.
 *  students — live enrolled headcount (student_count from getSectionLicenses API response)
 *  used     — module-linking consumption (licenses.used on backend),
 *             0 until a module is linked; drives "# Used/Available" column
 *  total    — class sub-pool size (section.licenses.total)
 */
const readClassLicenseForRow = (sectionRow, sectionLicensesFromApi) => {
    const fallback = readSectionLicenseStats(sectionRow);
    // students comes from the live API response (student_count field)
    const students = readSectionStudentCount(sectionLicensesFromApi);
    // Use licenses.used (module-linking deductions), NOT additional_details
    let used  = fallback.used;
    let total = fallback.total;
    if (sectionLicensesFromApi && typeof sectionLicensesFromApi === 'object') {
        const apiUsed  = Number(sectionLicensesFromApi.used);
        const apiTotal = Number(sectionLicensesFromApi.total);
        if (Number.isFinite(apiUsed))  used  = apiUsed;
        if (Number.isFinite(apiTotal)) total = apiTotal;
    }
    return { used, total, students };
};

const isSectionRow = isOrgSectionRow;

/** Class-table page sizes (server-side pagination). */
const CLASS_PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_CLASS_PAGE_SIZE = 10;

/** Previous/Next pager buttons: brand orange, and ALWAYS the same orange — the
 * disabled state is just a faded version of the same color (opacity only), so
 * stepping a page never changes the OTHER button's color, only its brightness. */
const paginationNavBtnSx = {
    textTransform: 'none',
    fontSize: '12.5px',
    color: '#FF6900',
    borderColor: '#FF6900',
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
    '&.Mui-disabled': { color: '#FF6900', borderColor: '#FF6900', opacity: 0.4 },
};

/**
 * Case-insensitive class search over the columns the table shows: class name,
 * grade level, and the class's school name. MIRRORS the server-side filter in
 * list_org_sections — used as the client fallback when an older backend
 * ignores the search param, and by the CSV download.
 */
const sectionMatchesSearch = (s, q, schoolNameByCode = {}) =>
    [
        s.section_name,
        s.section_level,
        schoolNameByCode[String(s.institute_code || '').trim()],
        s.section_incharge_name,
    ].some((v) => String(v || '').toLowerCase().includes(q));

/** Pagination meta from the sections/list response (E2EE nests under data.data). */
const readSectionsListMeta = (secRes) => {
    const d = secRes?.data;
    const src = d?.tableData ? d : d?.data?.tableData ? d.data : d || {};
    return { page: src?.page != null ? Number(src.page) : null, total: Number(src?.total) };
};

/** Map module_type to lp | assessment. */
const linkedModuleKind = (moduleType) => {
    const u = String(moduleType || '').toUpperCase().replace(/\s+/g, '_');
    if (u === 'LEARNING_PROGRAM') return 'lp';
    if (u === 'ASSESSMENT' || u === 'STANDARD_ASSESSMENT') return 'assessment';
    return null;
};

const moduleCodesFromBuckets = (linkedData, kind, buckets) => {
    if (!linkedData || typeof linkedData !== 'object') return [];
    const acc = new Set();
    for (const b of buckets) {
        const arr = linkedData[b];
        if (!Array.isArray(arr)) continue;
        for (const m of arr) {
            if (m?.module_code && linkedModuleKind(m.module_type) === kind) acc.add(String(m.module_code));
        }
    }
    return [...acc];
};

const linkedCodesBreakdownForKind = (linkedData, kind) => {
    const sectionLinked = moduleCodesFromBuckets(linkedData, kind, ['section_exclusive', 'multi_section']);
    const orgWide = moduleCodesFromBuckets(linkedData, kind, ['org_wide']);
    const all = [...new Set([...sectionLinked, ...orgWide])];
    return { sectionLinked, orgWide, all };
};

/** API #6 buckets for View class dialog. */
const VIEW_LINK_BUCKETS = [
    { key: 'section_exclusive', label: 'This class only' },
    { key: 'multi_section', label: 'Shared across classes' },
    { key: 'org_wide', label: 'Org-wide' },
];

const formatViewModuleType = (moduleType) => {
    const k = linkedModuleKind(moduleType);
    if (k === 'lp') return 'Learning program';
    if (k === 'assessment') return 'Assessment';
    return 'Module';
};

/** Insider + org module lists merged by code (org rows enrich names). */
const mergeCatalogByCode = (insiderRows, orgRows) => {
    const byCode = new Map();
    for (const row of insiderRows || []) {
        if (!row?.code) continue;
        const code = String(row.code);
        const merged = { ...row };
        if (merged.is_ready == null) merged.is_ready = true;
        byCode.set(code, merged);
    }
    for (const row of orgRows || []) {
        if (!row?.code) continue;
        const code = String(row.code);
        byCode.set(code, { ...(byCode.get(code) || {}), ...row, code });
    }
    return [...byCode.values()];
};

const orgModuleToPickerRow = (m, kind) => ({
    code: m.module_code,
    full_name: m.module_name,
    length_questions: kind === 'assessment' ? '' : '—',
    time_to_complete_minutes: String(m.duration || '').replace(/\s*min/i, '').trim() || '—',
    image_url: m.icon_url || null,
    is_ready: true,
    // Org catalog modules are only reachable here once already published (a program
    // must be published before it can be made visible/linkable at all), so default to
    // Published unless the backend explicitly says otherwise.
    status: m.status ?? LEARNING_PROGRAM_STATUS.PUBLISHED,
});

/** Class picker: section linked-modules (API #6) + org modules catalog + org-level linked programs. */
const loadClassLinkPicker = async (orgCode, sectionCode, kind, instituteCode = null) => {
    const moduleType = kind === 'lp' ? 'learning_program' : 'assessment';
    // instituteCode: a network linking one of its schools' classes sees only what
    // it made available to that school (partner -> network -> school -> class).
    const institute_code = instituteCode && instituteCode !== 'default' ? instituteCode : null;
    const [modsRes, orgModsRes] = await Promise.all([
        getSectionLinkedModulesService(orgCode, sectionCode),
        getOrgModulesService({ org_code: orgCode, module_type: moduleType, page: 1, limit: 200, institute_code }),
    ]);
    let linkedData = modsRes?.data;

    // For assessments: only show YoungPeople modules in the picker — those are the only ones
    // that appear in the student portal. Assessments for Teachers/Parents/etc. would be
    // confusingly listed even though students can't see them.
    // Already-linked items still show as "Already Linked" via mergePickerCatalogWithLinkedModules
    // (they come from getSectionLinkedModulesService, not the org catalog).
    const orgModules = orgModsRes?.data?.modules || [];
    const filteredModules = kind === 'assessment'
        ? orgModules.filter((m) => !m.user_group || m.user_group === USER_GROUPS.YOUNGPEOPLE)
        : orgModules;
    const orgCatalog = filteredModules.map((m) => orgModuleToPickerRow(m, kind));

    let catalog;
    if (kind === 'lp') {
        // Catalog: org modules from getOrgModulesService.
        // Linked state: section/linked_modules only — this is the ground truth.
        // getLinkedLearningProgramService returns org-level activated programs, NOT section-level
        // linked state. Injecting those into org_wide was wrongly marking unlinked programs as
        // "already linked" to sections. Only section/linked_modules.org_wide reflects actual
        // org-wide visibility to sections.
        catalog = orgCatalog;
    } else {
        // Assessment picker: org catalog filtered to YoungPeople + section/linked_modules for linked state.
        catalog = orgCatalog;
    }
    const rows = mergePickerCatalogWithLinkedModules(catalog, linkedData, kind);
    const breakdown = linkedCodesBreakdownForKind(linkedData, kind);
    return { rows, breakdown };
};

const applyOrgLicensesFromLinkResponse = (response, setPrimaryClient) => {
    const lic = response?.data?.licenses_dq_assess;
    if (lic && typeof lic === 'object') {
        patchClientLicensesFromDqpInfo(setPrimaryClient, lic);
    }
};

const reportSectionLinkResponse = (response, successMessage) => {
    const warnings = response?.data?.warnings;
    if (Array.isArray(warnings) && warnings.length) {
        toast(TOAST_TYPES.WARN, 'Classes', warnings[0]);
    }
    const added = Number(response?.data?.added_to_section) || 0;
    if (response?.data?.success && added > 0) {
        toast(TOAST_TYPES.SUCCESS, 'Classes', successMessage);
        return true;
    }
    if (response?.data?.success && added === 0 && (!warnings || !warnings.length)) {
        toast(TOAST_TYPES.SUCCESS, 'Classes', successMessage);
        return true;
    }
    return false;
};

const mergePickerCatalogWithLinkedModules = (catalog, linkedData, kind) => {
    const byCode = new Map();
    for (const row of catalog || []) {
        if (!row?.code) continue;
        const code = String(row.code);
        const merged = { ...row };
        if (merged.is_ready == null) merged.is_ready = true;
        if (merged.status == null) merged.status = LEARNING_PROGRAM_STATUS.PUBLISHED;
        byCode.set(code, merged);
    }
    if (!linkedData || typeof linkedData !== 'object') return [...byCode.values()];
    for (const b of ['section_exclusive', 'multi_section', 'org_wide']) {
        const arr = linkedData[b];
        if (!Array.isArray(arr)) continue;
        for (const m of arr) {
            if (!m?.module_code || linkedModuleKind(m.module_type) !== kind) continue;
            const code = String(m.module_code);
            if (!byCode.has(code)) {
                byCode.set(code, {
                    code,
                    full_name: m.module_name || code,
                    length_questions: '—',
                    time_to_complete_minutes: '—',
                    image_url: null,
                    is_ready: true,
                    // Already linked to a section/org — necessarily published.
                    status: LEARNING_PROGRAM_STATUS.PUBLISHED,
                });
            }
        }
    }
    return [...byCode.values()];
};

/**
 * A classes template's rows for ONE existing class (Manage Students): the rows
 * with its Class Name — and, in the network file, its school's School Email.
 */
const templateRowsOfClass = (data, kind, cls) => {
    const key = (v) => String(v || '').trim().toLowerCase();
    const blocks = (data || []).filter((b) => kind !== TEMPLATE_NETWORK_CLASS
        || !cls?.instituteCode
        || String(b?.head?.[0]?.institute_code || '') === cls.instituteCode);
    const rows = blocks.flatMap((b) => (Array.isArray(b?.license) ? b.license : []))
        .filter((lic) => key(lic.class_name) === key(cls?.name));
    return { blocks, rows };
};

const tabsToClassPayload = (classList) =>
    classList
        .map((tab) => ({
            className: (tab.className || '').trim(),
            gradeLevel: (tab.gradeLevel || '').trim(),
            // Saved with the class — it used to be typed in and then dropped.
            academicYear: (tab.academicYear || '').trim(),
            teacherFirstName: (tab.teacherFirstName || '').trim(),
            teacherLastName: (tab.teacherLastName || '').trim(),
            teacherEmail: (tab.teacherEmail || '').trim(),
        }))
        .filter((c) => c.className);

/** Enough pool seats for link UI (same as Client List). */
const hasEnoughLicenses = (client) => {
    if (!client || client.licenses_total === 0) {
        return false;
    }
    const licensesRemaining = client.licenses_total - client.licenses_used;
    return licensesRemaining >= client.users;
};

const countStudentsInTabs = (classList) => {
    let n = 0;
    for (const tab of classList || []) {
        for (const s of tab.students || []) {
            if (String(s.studentEmail || s.parentEmail || '').trim()) n += 1;
        }
    }
    return n;
};

/** Block enroll when org-wide modules need more class-pool seats than available. */
const getEnrollOrgWideLicenseCheck = (classRow, linkedData, studentCount) => {
    const orgWideCount = Array.isArray(linkedData?.org_wide) ? linkedData.org_wide.length : 0;
    if (!orgWideCount || !studentCount) return { ok: true, title: '' };
    const total = classRow?.licenseStats?.total || 0;
    const used = classRow?.licenseStats?.used || 0;
    if (total === 0) {
        return { ok: false, title: 'No licenses assigned to this class. Click "Add Licenses" before adding students.' };
    }
    const seatsNeeded = orgWideCount * studentCount;
    const available = Math.max(0, total - used);
    if (seatsNeeded > available) {
        return {
            ok: false,
            title:
                `Not enough licenses for ${studentCount} student${studentCount !== 1 ? 's' : ''} ` +
                `with ${orgWideCount} org-wide module${orgWideCount !== 1 ? 's' : ''}. ` +
                `Need ${seatsNeeded}, only ${available} available. Click "Add Licenses".`,
        };
    }
    return { ok: true, title: '' };
};

const ManageClasses = () => {
    useGeo(); // re-render with the API's lists once they load
    const { authUser, setAuthUser } = useAuth();
    /*
     * No per-class Licenses column for anyone now: a network's licenses are one
     * shared pool (Ian-SNW-29Sep-15), and a school's classes aren't assigned
     * licenses either (Ian-SW-29Sep-6). A teacher never had the column.
     */
    const hideClassLicenses = true;
    // School and network get the centred "My Classes" header; a teacher keeps its own.
    const centredClassesHeader = !isTeacher(authUser);
    const classes = useStyles({
        hideLicenses: hideClassLicenses,
        showSchool: !!authUser?.is_school_network,
        wideActions: !isTeacher(authUser),
        // School and network only — a teacher has no LearnCreator team to count.
        showLcTeachers: !isTeacher(authUser),
    });
    const location = useLocation();
    // A user may link a learning program to a class when granted the `link` permission,
    // regardless of access level.
    const canLinkLearningProgram = canLinkProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    // The class chooser's Assessment card follows the school's own assessment
    // products — a school holding COS (cpc_index_assessment) or the standard
    // assessment links it to its classes whatever else it has, LearnCreator
    // included. Not for teachers: they link only the programs they built.
    const canLinkAssessment = !isTeacher(authUser)
        && (canLinkProduct(authUser, PRODUCT_KEY.ASSESSMENT)
            || canLinkProduct(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT));
    // LC teacher fields are only usable when the partner has actually assigned
    // LearnCreator to this school/network (PAC product_access.learning_program
    // .enabled, set via the partner's "Link Products" > Assign LearnCreator
    // flow) AND the workspace is on one of the two plans that carry LearnCreator:
    // the standalone LearnCreator plan or enterprise_p1 (not p1a/p1b).
    const LC_ENABLED_PLAN_KEYS = [DQ_PRO_PLAN_KEY.ENTERPRISE_P1, DQ_PRO_PLAN_KEY.ENTERPRISE_LC];
    const lcActivated = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
        && canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM)
        && LC_ENABLED_PLAN_KEYS.includes(solutionPlanKey(authUser?.dq_pro_plan?.plan_key))
        && authUser?.dq_pro_plan?.status === 'active';
    const [busy, setBusy] = useState(false);
    const [orgCode, setOrgCode] = useState(null);
    const [orgName, setOrgName] = useState(null);
    const [orgType, setOrgType] = useState(ORG_TYPE_SCHOOL);
    const [primaryClient, setPrimaryClient] = useState(null);
    const [classRows, setClassRows] = useState([]);

    // Publish Learning Space (My Classes deck 30 Sep 2026, C1-C8). `publishStep`
    // says which screen is showing; the rest is what they render.
    const [publishStep, setPublishStep] = useState(null); // 'scope' | 'confirm' | 'short' | 'done'
    const [publishLoading, setPublishLoading] = useState(false);
    const [publishSelection, setPublishSelection] = useState(null);
    const [publishShortfall, setPublishShortfall] = useState(null);
    const [publishResult, setPublishResult] = useState(null);
    const [publishResetToken, setPublishResetToken] = useState(0);

    // The per-class / per-grade summary from the learning-space preview. Both the
    // publish dialog and Manage LearnCreator Teachers read it: it is the one call
    // that carries every class with its grade, learner count and publication
    // state — the paged class table only ever holds one page.
    const EMPTY_SUMMARY = { classes: [], grades: [], licenses: { total: 0, used: 0, available: 0 }, org_name: '', open_licence_requests: [] };
    const [classSummary, setClassSummary] = useState(EMPTY_SUMMARY);
    const [classSummaryLoading, setClassSummaryLoading] = useState(false);

    // LearnCreator teachers (deck A1-A13, B1-B3).
    const [lcTeachers, setLcTeachers] = useState([]);
    const [lcTeachersLoading, setLcTeachersLoading] = useState(false);
    const [lcDialogOpen, setLcDialogOpen] = useState(false);
    // ── Class table: server-side search + pagination ────────────────────────
    const [classSearch, setClassSearch] = useState('');            // input value
    const [classSearchApplied, setClassSearchApplied] = useState(''); // debounced
    const [classPage, setClassPage] = useState(1);
    const [classPageSize, setClassPageSize] = useState(DEFAULT_CLASS_PAGE_SIZE);
    const [classTotal, setClassTotal] = useState(0); // filtered total (all pages)
    const classTotalPages = Math.max(1, Math.ceil(classTotal / classPageSize));

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
    const [enrollClassRow, setEnrollClassRow] = useState(null);
    // { jobId, done, total } while a server-side (async job) CSV upload runs.
    // The upload continues in the background — no dialog is held open — and
    // progress shows in a floating banner at the bottom of the page.
    const [bgUpload, setBgUpload] = useState(null);
    // Org's schools (INSTITUTE rows) — feeds the Add Class / enroll School picker.
    const [schools, setSchools] = useState([]);
    const [enrollLinkedData, setEnrollLinkedData] = useState(null);
    const [enrollLinkedLoading, setEnrollLinkedLoading] = useState(false);
    // "Edit Details" — class details only, no roster. A
    // separate dialog/flow from "Manage Students" (isEnrollDialogOpen above).
    const [isEditClassDialogOpen, setIsEditClassDialogOpen] = useState(false);
    const [editClassRow, setEditClassRow] = useState(null);
    const [viewClassRow, setViewClassRow] = useState(null);
    const [viewLinkedData, setViewLinkedData] = useState(null);
    const [viewLinkedLoading, setViewLinkedLoading] = useState(false);

    const [assessments, setAssessments] = useState([]);
    const [learningPrograms, setLearningPrograms] = useState([]);

    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignSectionRow, setAssignSectionRow] = useState(null);
    const [assignLicenseStats, setAssignLicenseStats] = useState(null);
    const [assignLicenseStatsLoading, setAssignLicenseStatsLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);

    const [linkTypeChooserOpen, setLinkTypeChooserOpen] = useState(false);
    const [linkSectionRow, setLinkSectionRow] = useState(null);

    const [linkAssessmentOpen, setLinkAssessmentOpen] = useState(false);
    const [linkedAssessmentCodes, setLinkedAssessmentCodes] = useState([]);
    const [orgWideAssessmentCodes, setOrgWideAssessmentCodes] = useState([]);
    const [sectionLinkedAssessmentCodes, setSectionLinkedAssessmentCodes] = useState([]);
    const [assessmentPickerRows, setAssessmentPickerRows] = useState([]);
    const [linkAssessmentListLoading, setLinkAssessmentListLoading] = useState(false);
    const [linkingAssessment, setLinkingAssessment] = useState(false);

    const [linkLearningOpen, setLinkLearningOpen] = useState(false);
    const [linkedLearningCodes, setLinkedLearningCodes] = useState([]);
    const [orgWideLearningCodes, setOrgWideLearningCodes] = useState([]);
    const [sectionLinkedLearningCodes, setSectionLinkedLearningCodes] = useState([]);
    const [lpPickerRows, setLpPickerRows] = useState([]);
    const [linkLearningListLoading, setLinkLearningListLoading] = useState(false);
    const [linkingLearning, setLinkingLearning] = useState(false);

    /*
     * SNW-ALL-My Classes-Main — a School Network sees an aggregate "My
     * Classes" across every school in its network, or a single school's own
     * classes when it picks one from the dropdown. FRONTEND ONLY for now, per
     * explicit instruction: the single-school loadClasses below enriches each
     * class with 2 extra calls (getSectionLicensesService +
     * getSectionLinkedModulesService); fanning that out across every class in
     * every network school could mean 50-100+ calls to render one page, so
     * the ALL-SCHOOLS table shows base fields only (name/grade/teacher/school)
     * and Students/Licenses/Programs read "—". Picking ONE school enriches them,
     * because that is the same per-class cost that school's own My Classes pays.
     * Stat cards use the cheap per-school rollups listClientsService already
     * returns (classes_count/users/licenses_total/licenses_used/
     * programs_linked), same fields DQProClientList.page.jsx's own netStats
     * already relies on.
     *
     * Add a Class IS retargeted: its "Which school?" picker (networkSchools
     * prop on AddClassStudentsDialog) resolves the real member-school
     * org_code that handleSave threads through postEnrollTabs/
     * persistClassPrefs, replacing the network's own
     * org_code that those calls used to receive by default. Edit/Manage
     * Students/Publish/Assign Licenses remain UNretargeted for now — same
     * "stays as-is until asked" flag as before, just narrowed to what's
     * actually still open.
     */
    /*
     * Not a teacher. An LC teacher of a network school logs in on the network's
     * org_code with no institute_code, so the session marks them a network —
     * and this page gave them the network's view: every class of every school,
     * the network's cards and buttons, and none of the narrowing to their own
     * classes (LA-SNW,TW-22Oct-01). The teacher list below already handles a
     * teacher in any school.
     */
    const viewerIsNetwork = !!authUser?.is_school_network && !isTeacher(authUser);
    const [networkSchools, setNetworkSchools] = useState([]);
    const [networkSchoolsLoading, setNetworkSchoolsLoading] = useState(false);
    const [selectedNetworkSchool, setSelectedNetworkSchool] = useState(''); // '' = All schools in the network
    /*
     * Which school, by the PAIR. Schools of a network share the network's
     * org_code — institute_code is what tells them apart — so keying the
     * dropdown on org_code alone selected the FIRST school whatever was picked
     * (title and stat cards of one school, classes of another), and a class
     * added "for" one school could land in another (bug sheet Ian-SNW-29Sep-13).
     */
    const networkSchoolKey = (s) => `${s?.org_code || ''}::${s?.institute_code || ''}`;
    const [networkClassRows, setNetworkClassRows] = useState([]);
    const [networkClassesLoading, setNetworkClassesLoading] = useState(false);

    useEffect(() => {
        if (!viewerIsNetwork) return undefined;
        let cancelled = false;
        setNetworkSchoolsLoading(true);
        listClientsService({ limit: 200 })
            .then((res) => {
                if (cancelled) return;
                const data = (res && res.data) || {};
                setNetworkSchools(Array.isArray(data.clients) ? data.clients : []);
            })
            .catch(() => { if (!cancelled) setNetworkSchools([]); })
            .finally(() => { if (!cancelled) setNetworkSchoolsLoading(false); });
        return () => { cancelled = true; };
    }, [viewerIsNetwork]);

    const loadNetworkClasses = useCallback(async () => {
        if (!viewerIsNetwork || !networkSchools.length) {
            setNetworkClassRows([]);
            return;
        }
        setNetworkClassesLoading(true);
        try {
            const targets = selectedNetworkSchool
                ? networkSchools.filter((s) => networkSchoolKey(s) === selectedNetworkSchool)
                : networkSchools;
            const results = await Promise.all(targets.map(async (school) => {
                try {
                    // institute_code, not just org_code: every school of a network
                    // now shares that code, so without it each school would come back
                    // with the whole network's classes.
                    const res = await listOrgSectionsService(school.org_code, {
                        limit: 500,
                        institute_code: school.institute_code,
                    });
                    if (res === false) return [];
                    const tableData = extractOrgSectionsTableData(res);
                    const sections = tableData.filter(isSectionRow);

                    /*
                     * Students, licences and programmes, per class.
                     *
                     * The same two calls and the same readers the single-school
                     * list uses — otherwise the two tables report the same class
                     * differently, and a network sees only em-dashes where its
                     * schools see numbers.
                     *
                     * Per class rather than per school because that is what the
                     * endpoints take. Failures are swallowed the same way: an
                     * informational column must never take the whole list down.
                     */
                    const licenseBySectionCode = new Map();
                    const programCountBySectionCode = new Map();

                    /*
                     * ONE school at a time only.
                     *
                     * Enrichment is 2 calls per class. For a single school that is
                     * the same cost the school's own My Classes already pays. Across
                     * a whole network it is 2 x every class in every school — the
                     * 50-100+ requests the note above refuses, and rightly. So "All
                     * schools" keeps the em-dashes and picking a school fills them
                     * in, which is also when a number is worth reading.
                     */
                    /*
                     * Except Students and Programs: "All schools" counts them too
                     * (LA, 22 Oct — a network saw numbers only after picking a
                     * school; programs, 23 Oct). The licences call carries the
                     * student count and the linked-modules call the programs, so
                     * both run for every class; licences stay one school only.
                     */
                    const enriched = !!selectedNetworkSchool;
                    await Promise.all(sections.map(async (sec) => {
                        const code = String(sec.section_code || sec._id || sec.id || '').trim();
                        if (!code) return;
                        try {
                            const licRes = await getSectionLicensesService(school.org_code, code);
                            if (licRes !== false && licRes?.data?.licenses) {
                                licenseBySectionCode.set(code, {
                                    ...licRes.data.licenses,
                                    student_count: licRes.data.student_count ?? 0,
                                });
                            }
                        } catch { /* row falls back to the sections/list values */ }
                        try {
                            const linkRes = await getSectionLinkedModulesService(school.org_code, code);
                            if (linkRes !== false && linkRes?.data) {
                                const buckets = ['section_exclusive', 'multi_section', 'org_wide'];
                                const count = buckets.reduce((n, key) => {
                                    const arr = Array.isArray(linkRes.data[key]) ? linkRes.data[key] : [];
                                    return n + arr.filter((m) => String(m.module_type || '').toUpperCase() === 'LEARNING_PROGRAM').length;
                                }, 0);
                                programCountBySectionCode.set(code, count);
                            }
                        } catch { /* leave uncounted — row falls back to 0 */ }
                    }));

                    return sections.map((s) => {
                        const sectionCode = String(s.section_code || s._id || s.id || '').trim();
                        const { used: classLicUsed, total: classLicTotal, students: classStudents } =
                            readClassLicenseForRow(s, licenseBySectionCode.get(sectionCode));
                        return {
                            /*
                             * `id` is the class's REAL code, exactly as the
                             * single-school list builds it — every action on a row
                             * (assign licences, link programs, edit, enrol) reads
                             * `.id` and sends it to the server as the section code.
                             * It used to be `org_code:section_code`, which named a
                             * class no org has.
                             *
                             * rowKey keeps that composite for React, where it IS
                             * needed: two schools of one network can hold classes
                             * whose codes would otherwise collide in one list.
                             */
                            id: sectionCode || s._id || s.id,
                            rowKey: `${school.org_code}:${sectionCode || s._id || s.id}`,
                            sectionCode,
                            lifecycleStatus: s.lifecycle_status,
                            // Ordering only — see the sort in the caller.
                            createdAt: s.createdAt || s.created_at || null,
                            name: s.section_name || s.name || '—',
                            grade: s.section_level || s.grade || '—',
                            academicYear: s.academic_year || '',
                            /*
                             * A dash where the number was never fetched.
                             *
                             * Enrichment only runs for ONE selected school (see the
                             * note above). Falling through to 0 across the whole
                             * network would read as "this class has no students" when
                             * it means "nobody counted" — the em-dash is the honest
                             * answer, and it is what this table showed before.
                             */
                            // "All schools": a dash only where the count could not be read.
                            students: (enriched || licenseBySectionCode.has(sectionCode)) ? String(classStudents) : '—',
                            licenses: enriched ? formatLicenseCell(classLicUsed, classLicTotal) : '—',
                            licenseStats: enriched ? { used: classLicUsed, total: classLicTotal } : null,
                            // Same rule as Students: a dash only where the count could not be read.
                            programCount: (enriched || programCountBySectionCode.has(sectionCode)) ? (programCountBySectionCode.get(sectionCode) ?? 0) : '—',
                            // The school this class hangs off, so the enroll and
                            // edit dialogs can name it the same way the
                            // single-school list does.
                            instituteCode: String(s.institute_code || '').trim(),
                            teacherName: String(s.section_incharge_name || '').trim(),
                            // Split as the single-school list does — Manage Students
                            // fills its template and its uploads with the teacher.
                            teacherFirstName: String(s.section_incharge_name || '').trim().split(/\s+/)[0] || '',
                            teacherLastName: String(s.section_incharge_name || '').trim().split(/\s+/).slice(1).join(' '),
                            teacherEmail: String(s.section_incharge_email || '').trim(),
                            schoolName: school.org_name,
                            schoolOrgCode: school.org_code,
                        };
                    });
                } catch {
                    return [];
                }
            }));
            /*
             * Newest first, like the single-school list — which reverses the
             * sections/list order because the API returns creation order,
             * oldest first. Without this a class you just added lands at the
             * BOTTOM of the network table and reads as not created.
             *
             * By createdAt where the row carries it, so classes from different
             * schools interleave by age rather than clumping per school.
             */
            const rows = results.flat();
            rows.sort((a, b) => {
                const ta = a.createdAt ? Date.parse(a.createdAt) : NaN;
                const tb = b.createdAt ? Date.parse(b.createdAt) : NaN;
                if (Number.isFinite(ta) && Number.isFinite(tb)) return tb - ta;
                /*
                 * Falls back to the row id, which is a Mongo ObjectId: its first
                 * four bytes ARE the creation time, so comparing the hex orders by
                 * age. That keeps the order right against an API that has not been
                 * redeployed with createdAt on the section payload yet.
                 */
                return String(b.id || '').localeCompare(String(a.id || ''));
            });
            setNetworkClassRows(rows);
        } finally {
            setNetworkClassesLoading(false);
        }
    }, [viewerIsNetwork, selectedNetworkSchool, networkSchools]);

    useEffect(() => { loadNetworkClasses(); }, [loadNetworkClasses]);

    const selectedSchoolRow = selectedNetworkSchool
        ? networkSchools.find((s) => networkSchoolKey(s) === selectedNetworkSchool) || null
        : null;

    const networkStats = (() => {
        const scope = selectedSchoolRow ? [selectedSchoolRow] : networkSchools;
        const sum = (key) => scope.reduce((total, s) => total + (Number(s[key]) || 0), 0);
        const licTotal = sum('licenses_total');
        const licUsed = sum('licenses_used');
        return {
            classes: sum('classes_count'),
            students: sum('users'),
            programs: sum('programs_linked'),
            licensesTotal: licTotal,
            licensesUsed: licUsed,
            licensesRemaining: Math.max(0, licTotal - licUsed),
        };
    })();

    const networkFilteredRows = networkClassRows.filter((c) => {
        const q = String(classSearchApplied || '').trim().toLowerCase();
        if (!q) return true;
        return [c.name, c.grade, c.teacherName, c.schoolName].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const effectiveClassRows = viewerIsNetwork ? networkFilteredRows : classRows;
    const effectiveBusy = viewerIsNetwork ? networkClassesLoading || networkSchoolsLoading : busy;

    // A teacher's own class codes, as one string — a dependency of loadClasses below.
    const teacherClassKey = isTeacher(authUser) ? membershipSectionCodes(authUser).join(',') : '';
    const loadClasses = useCallback(async () => {
        const code = authUser?.org_code ? String(authUser.org_code).trim() : '';
        if (!code) {
            setOrgCode(null);
            setOrgName(null);
            setOrgType(ORG_TYPE_SCHOOL);
            setPrimaryClient(null);
            setClassRows([]);
            return;
        }

        setBusy(true);
        try {
            setOrgCode(code);

            const [secRes, orgDetailsRes] = await Promise.all([
                /*
                 * A teacher asks for the full list. The server pages the SCHOOL's
                 * classes, and only then are they narrowed to the teacher's own —
                 * so a teacher whose class is not among the school's newest 10
                 * saw nothing, under a Classes card counting the whole school.
                 * Without page/search the client-side path below filters and
                 * pages the teacher's classes instead.
                 */
                listOrgSectionsService(code, isTeacher(authUser) ? {} : {
                    search: classSearchApplied,
                    page: classPage,
                    limit: classPageSize,
                }),
                getOrgDetailsService(code),
            ]);

            const org = unwrapOrgFromDetailsRes(orgDetailsRes);
            setOrgName(org?.org_name || authUser?.org_name || null);
            setOrgType(org?.type != null ? org.type : authUser?.org_type != null ? authUser.org_type : ORG_TYPE_SCHOOL);

            const licenseClient = buildOrgLicenseClient(org?.licenses_dq_assess);
            setPrimaryClient(licenseClient);

            if (secRes === false) {
                toast(
                    TOAST_TYPES.WARN,
                    'Classes',
                    'Could not load classes (session encryption). Log out, sign in again, then refresh this page.',
                );
                setClassRows([]);
                return;
            }
            const tableData = extractOrgSectionsTableData(secRes);
            // A teacher only sees the classes bound to their own membership —
            // class-scoped actions are checked server-side, but the teacher
            // must never even see a class they cannot open (spec §6).
            const sections = tableData.filter(isSectionRow).filter((s) =>
                canActOnSection(authUser, String(s.section_code || '').trim()));

            // Schools (INSTITUTE rows) for the dialog's School picker — the
            // legacy 'default' shell row is an implementation detail, not a school.
            // The server sends ALL institute rows even when paginating sections.
            const instituteRows = tableData.filter(
                (r) => Number(r.details_type) === 0 && r.institute_code,
            );
            setSchools(instituteRows.filter((r) => r.institute_code !== 'default'));
            // institute_code → school name, to label each class row.
            const schoolNameByCode = {};
            instituteRows.forEach((r) => {
                schoolNameByCode[r.institute_code] = r.institute_name || r.institute_code;
            });

            // Deploy-order safety: an older backend ignores search/page/limit and
            // returns the FULL roster (and echoes no `page`). Detect that and
            // filter/paginate client-side so the table works either way. In
            // server mode the section rows arrive newest-first and pre-sliced;
            // in fallback mode we reverse (creation order is oldest-first) and
            // slice here — either way `pageSections` is newest-first, page-only.
            const meta = readSectionsListMeta(secRes);
            const serverMode = meta.page != null;
            let pageSections = sections;
            let total = Number.isFinite(meta.total) ? meta.total : sections.length;
            if (!serverMode) {
                const q = String(classSearchApplied || '').trim().toLowerCase();
                const filtered = q
                    ? sections.filter((s) => sectionMatchesSearch(s, q, schoolNameByCode))
                    : sections;
                total = filtered.length;
                pageSections = [...filtered]
                    .reverse()
                    .slice((classPage - 1) * classPageSize, classPage * classPageSize);
            }
            setClassTotal(total);
            // Search/page-size shrank the result set below the current page
            // (e.g. page 3 of an 8-row search) → snap back to page 1, which
            // re-runs this loader with rows to show.
            if (classPage > 1 && pageSections.length === 0 && total > 0) {
                setClassPage(1);
            }

            const licenseBySectionCode = new Map();
            const programCountBySectionCode = new Map();
            await Promise.all(
                pageSections.map(async (s) => {
                    const sectionCode = String(s.section_code || s._id || s.id || '').trim();
                    if (!sectionCode) return;
                    try {
                        const licRes = await getSectionLicensesService(code, sectionCode);
                        if (licRes !== false && licRes?.data?.licenses) {
                            // Flatten licenses fields + student_count into one object so that
                            // readClassLicenseForRow can read .used, .total, and .student_count
                            // from the same object without nested access.
                            licenseBySectionCode.set(sectionCode, {
                                ...licRes.data.licenses,           // total, used
                                student_count: licRes.data.student_count ?? 0,
                            });
                        }
                    } catch {
                        /* fall back to sections/list row */
                    }
                    // "PROGRAMS" list column — count of learning programs linked to
                    // this class (excludes assessments), across all three visibility
                    // buckets the linked-modules endpoint already returns. Same data
                    // the row's own dialog already fetches on demand; just fetched
                    // per-row here too so the list can show it up front.
                    try {
                        const linkRes = await getSectionLinkedModulesService(code, sectionCode);
                        if (linkRes !== false && linkRes?.data) {
                            const buckets = ['section_exclusive', 'multi_section', 'org_wide'];
                            const count = buckets.reduce((n, key) => {
                                const arr = Array.isArray(linkRes.data[key]) ? linkRes.data[key] : [];
                                return n + arr.filter((m) => String(m.module_type || '').toUpperCase() === 'LEARNING_PROGRAM').length;
                            }, 0);
                            programCountBySectionCode.set(sectionCode, count);
                        }
                    } catch {
                        /* leave uncounted — row falls back to 0 */
                    }
                }),
            );

            setClassRows(
                // pageSections is already newest-first (server mode pre-sorts;
                // fallback mode reversed above) — no extra ordering needed here.
                pageSections.map((s) => {
                    const sectionCode = String(s.section_code || s._id || s.id || '').trim();
                    const { used: classLicUsed, total: classLicTotal, students: classStudents } = readClassLicenseForRow(
                        s,
                        licenseBySectionCode.get(sectionCode),
                    );
                    // # Students column = actual enrolled headcount (separate from license usage)
                    const studentsDisplay = String(classStudents);
                    // # Used/Available column = 0 used until a module is linked and consumed
                    const licensesDisplay = formatLicenseCell(classLicUsed, classLicTotal);
                    const inchargeName = String(s.section_incharge_name || '').trim();
                    const nameParts = inchargeName ? inchargeName.split(/\s+/) : [];
                    return {
                        id: sectionCode || s._id || s.id,
                        sectionCode,
                        // Read through the badge helper, not off the document: a
                        // class created before the lifecycle existed has no
                        // status and is live.
                        lifecycleStatus: s.lifecycle_status,
                        name: s.section_name || s.name || '—',
                        grade: s.section_level || s.grade || '—',
                        academicYear: s.academic_year || '',
                        students: studentsDisplay,
                        licenses: licensesDisplay,
                        licenseStats: { used: classLicUsed, total: classLicTotal },
                        programCount: programCountBySectionCode.get(sectionCode) ?? 0,
                        teacherName: inchargeName,
                        teacherFirstName: nameParts[0] || '',
                        teacherLastName: nameParts.slice(1).join(' ') || '',
                        teacherEmail: String(s.section_incharge_email || '').trim(),
                        // Persisted class prefs (unwrapped from additional_details
                        // by the sections list) — prefill the enroll dialog.
                        usernamePrefix: String(s.username_prefix || '').trim(),
                        schoolPostalCode: String(s.school_postal_code || '').trim(),
                        // School (INSTITUTE) this class belongs to — drives the
                        // enroll dialog's School picker + move-on-edit.
                        instituteCode: String(s.institute_code || '').trim(),
                        // School display name — distinguishes same-named classes
                        // that live in different schools.
                        schoolName: schoolNameByCode[String(s.institute_code || '').trim()] || '',
                    };
                }),
            );
        } catch (e) {
            setClassRows([]);
        }
        setBusy(false);
    // teacherClassKey: a teacher's own classes — reload when they change (see the effect
    // that re-reads them below), so a class added or removed shows at once.
    }, [authUser?.org_code, authUser?.org_name, authUser?.org_type, primaryClient?.licenses_used, primaryClient?.licenses_total, classSearchApplied, classPage, classPageSize, teacherClassKey]);

    /*
     * Both lists, because only one of them is on screen.
     *
     * A network viewer reads networkClassRows; a school viewer reads classRows.
     * After adding a class or uploading a roster, refreshing only the
     * single-school list left a network looking at a stale table until the page
     * was reloaded by hand.
     */
    const refreshClasses = useCallback(
        async () => { await Promise.all([loadClasses(), loadNetworkClasses()]); },
        [loadClasses, loadNetworkClasses],
    );

    // Debounced search: typing re-queries the server 400ms after the last
    // keystroke (same cadence as the enroll dialog's roster search) and always
    // restarts from page 1. loadClasses itself re-runs via its deps.
    useEffect(() => {
        const t = setTimeout(() => {
            setClassSearchApplied(String(classSearch || '').trim());
            setClassPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [classSearch]);

    const handleDownloadExistingData = useCallback(async () => {
        if (!orgCode) {
            toast(TOAST_TYPES.WARN, 'Classes', 'No client organization loaded.');
            return;
        }
        setBusy(true);
        try {
            // City: a column of the classes and learners template (13 Oct 2026).
            const keys = orgType === ORG_TYPE_SCHOOL
                ? [...getExistingUsersExportKeys(orgType), 'city']
                : getExistingUsersExportKeys(orgType);
            const q = String(classSearchApplied || '').trim().toLowerCase();
            // The table shows only one PAGE of classes, so the export re-fetches
            // the FULL sections list (server-filtered by the active search) —
            // it supplies the complete code→name maps and, when a search is
            // active, the set of matching classes to export.
            const [res, secRes] = await Promise.all([
                analyticsGetUsersCsvService({ org_code: orgCode, keys }),
                listOrgSectionsService(orgCode, q ? { search: q } : {}),
            ]);
            if (res === false) {
                toast(TOAST_TYPES.WARN, 'Classes', 'Could not download. Log out, sign in again, and retry.');
                return;
            }
            let sheetsData = Array.isArray(res?.data) ? res.data : [];
            // A school's export also lists its classes with no learners yet (the
            // import template's own rule), so only a company stops here.
            if (orgType !== ORG_TYPE_SCHOOL && !sheetsData.length) {
                toast(TOAST_TYPES.WARN, 'Classes', 'No student data found to download.');
                return;
            }
            // Backend returns SCH_/SEC_ codes in the name columns; translate them
            // back to readable names. Unmapped codes fall through unchanged in
            // the builder.
            const secTableData = secRes === false ? [] : extractOrgSectionsTableData(secRes);
            const schoolNameByCode = {};
            secTableData
                .filter((r) => Number(r.details_type) === 0 && r.institute_code)
                .forEach((r) => {
                    schoolNameByCode[r.institute_code] = r.institute_name || r.institute_code;
                });
            const classNameByCode = {};
            // Client fallback: an older backend ignores `search` and returns every
            // section — re-apply the same filter so the export still honors it.
            const matchedSections = secTableData.filter(isSectionRow)
                .filter((s) => !q || sectionMatchesSearch(s, q, schoolNameByCode));
            matchedSections.forEach((s) => {
                const code = String(s.section_code || '').trim();
                const nm = String(s.section_name || '').trim();
                if (code && nm) classNameByCode[code] = nm;
            });
            if (q) {
                // Searched download: keep only students of the matching classes.
                const matchedCodes = new Set(Object.keys(classNameByCode));
                sheetsData = sheetsData
                    .map((block) => ({
                        ...block,
                        license: (block.license || []).filter((lic) =>
                            matchedCodes.has(String(lic.class_name || '').trim())),
                    }))
                    .filter((block) => block.license.length > 0);
                if (!sheetsData.length && !(orgType === ORG_TYPE_SCHOOL && matchedSections.length)) {
                    toast(TOAST_TYPES.WARN, 'Classes', 'No students found for the current search.');
                    return;
                }
            }
            if (orgType === ORG_TYPE_SCHOOL) {
                if (!sheetsData.length && !matchedSections.length) {
                    toast(TOAST_TYPES.WARN, 'Classes', 'No classes found to download.');
                    return;
                }
                /*
                 * The classes and learners import template itself, filled in
                 * (Ian, 13 Oct 2026: one file for importing and exporting). A
                 * network's names each class's school by its Contact Email.
                 */
                const schoolEmailByCode = {};
                secTableData
                    .filter((r) => Number(r.details_type) === 0 && r.institute_code && r.institute_head_email)
                    .forEach((r) => { schoolEmailByCode[r.institute_code] = r.institute_head_email; });
                (networkSchools || []).forEach((s) => {
                    if (s.institute_code && s.contact_email) schoolEmailByCode[s.institute_code] = s.contact_email;
                });
                const blob = await buildClassImportExportBlob({
                    network: viewerIsNetwork, sheetsData, classNameByCode, schoolEmailByCode, sections: matchedSections,
                });
                saveTemplateBlob(blob, 'Class Details');
                return;
            }
            const wb = await buildExistingUsersWorkbook(sheetsData, orgType, { schoolNameByCode, classNameByCode });
            // Named after its button, like School Details / Network and School Details.
            await downloadExcelJsWorkbook(wb, 'Class Details');
        } catch (err) {
            toast(TOAST_TYPES.WARN, 'Classes', err?.message || 'Download failed.');
        } finally {
            setBusy(false);
        }
    }, [orgCode, orgType, classSearchApplied, viewerIsNetwork, networkSchools]);

    const handleDownloadLicenseTemplate = useCallback(async (targetClassName, prefillSchool = null, isEnrollMode = false) => {
        if (!orgCode) {
            toast(TOAST_TYPES.WARN, 'Classes', 'No client organization loaded.');
            return;
        }
        setBusy(true);
        try {
            /*
             * Add a Class and Manage Students both take Ian's classes and
             * learners template (13 Oct 2026). Manage Students gets it with the
             * open class's details on the first row — what its older template
             * filled in — so only the learner columns are left to type.
             */
            if (orgType === ORG_TYPE_SCHOOL) {
                const kind = viewerIsNetwork ? TEMPLATE_NETWORK_CLASS : TEMPLATE_SCHOOL_CLASS;
                if (isEnrollMode && enrollClassRow) {
                    const known = (v) => (String(v || '').trim() === '—' ? '' : String(v || '').trim());
                    const school = viewerIsNetwork
                        ? networkSchools.find((ns) => String(ns.institute_code || '') === enrollClassRow.instituteCode)
                        : null;
                    const blob = await fillImportTemplate(kind, [{
                        school_email: school ? (school.contact_email || school.institute_head_email || '') : '',
                        class_name: known(enrollClassRow.name),
                        grade_level: known(enrollClassRow.grade),
                        academic_year: known(enrollClassRow.academicYear),
                        teacher_first_name: enrollClassRow.teacherFirstName,
                        teacher_last_name: enrollClassRow.teacherLastName,
                        teacher_email: enrollClassRow.teacherEmail,
                    }]);
                    saveTemplateBlob(blob, IMPORT_TEMPLATES[kind].file);
                } else {
                    await downloadImportTemplate(kind);
                }
                setBusy(false);
                return;
            }
            const res = await analyticsLicensesInfoService(orgCode);
            if (res === false) {
                toast(
                    TOAST_TYPES.WARN,
                    'Classes',
                    'Could not load license info. Log out, sign in again, and retry.',
                );
                return;
            }
            // Adding students to an EXISTING class never needs a head-teacher block —
            // the class already has its teacher, and a stale/placeholder value there
            // was what caused a re-upload to silently create a duplicate class (see
            // handleLicenseCsvUpload's incharge_* override below).
            const wb = await buildStudentUploadTemplateWorkbook(res?.data, orgType, targetClassName || undefined, prefillSchool || null, isEnrollMode);
            await downloadExcelJsWorkbook(wb, 'profiles_data');
        } catch (err) {
            toast(TOAST_TYPES.WARN, 'Classes', err?.message || 'Template download failed.');
        }
        setBusy(false);
    }, [orgCode, orgType, viewerIsNetwork, enrollClassRow, networkSchools]);

    const handleLicenseCsvUpload = useCallback(
        async (file, sectionInfo = null) => {
            if (!orgCode) return false;
            /*
             * A network adds a class for ONE of its schools — the one picked in
             * the dialog (bug sheet Ian-SNW-29Sep-9). Same resolution as
             * handleSave: the picker hands back the school's pair key, and the
             * school's institute_code is what files the class under it.
             * Adding students to an existing class (enroll) is not retargeted.
             */
            const networkCreate = viewerIsNetwork && !enrollClassRow;
            const networkTarget = networkCreate
                ? networkSchools.find((s) => networkSchoolKey(s) === String(sectionInfo?.networkSchoolKey || '').trim()) || null
                : null;
            /*
             * …unless the file is the network's classes and learners template
             * (Ian, 13 Oct 2026): its School Email column names each class's
             * school, so one file can hold classes for several schools.
             */
            const fileKind = networkCreate && !networkTarget
                ? await importTemplateKindOfFile(file).catch(() => null)
                : null;
            if (networkCreate && !networkTarget && fileKind !== TEMPLATE_NETWORK_CLASS) {
                // A network with no schools yet has nothing to pick from — say
                // what to do, instead of asking for a choice that is not on screen.
                // A schools file is in the wrong place whatever is picked.
                toast(TOAST_TYPES.WARN, 'Classes', fileKind && fileKind !== TEMPLATE_SCHOOL_CLASS
                    ? 'This is the schools template — upload it from My Schools. Classes take the classes and learners template.'
                    : networkSchools.length
                    ? 'Choose a school for this class.'
                    : 'Add a school first — a class belongs to one of your schools. Go to My Schools to add or upload schools.');
                return false;
            }
            const targetOrgCode = String(networkTarget?.org_code || '').trim() || orgCode;
            if (bgUpload) {
                toast(TOAST_TYPES.WARN, 'Classes', 'A student upload is already running — please wait for it to finish before starting another.');
                return false;
            }
            setBusy(true);
            try {
                // School picker selection travels on sectionInfo.school; enroll
                // mode falls back to the open class's current school.
                const uploadSchool = sectionInfo?.school
                    || (enrollClassRow?.instituteCode && enrollClassRow.instituteCode !== 'default'
                        ? { institute_code: enrollClassRow.instituteCode }
                        : null);
                const {
                    data, error, stackedClasses, importTemplate: fileTemplate, emptyClasses,
                } = await parseStudentLicenseWorkbookToPostData(file, undefined, {
                    // The network's schools, which a classes template's School Email picks from.
                    networkSchools,
                    // The school picked on Add a Class: every row of the file must be for it.
                    pickedSchool: networkTarget,
                    school: uploadSchool,
                    schools, // saved schools — lets the file's school block match an existing one
                    // forceSchool (enroll) locks the upload to the class's own school,
                    // ignoring any school block in the file so the class isn't moved/duplicated.
                    // A network's picked school wins the same way.
                    forceSchool: sectionInfo?.forceSchool
                        || (networkTarget?.institute_code ? { institute_code: networkTarget.institute_code } : null),
                });
                if (error) {
                    toast(TOAST_TYPES.WARN, 'Classes', error);
                    return;
                }
                /*
                 * An Export Class Details file (every class, one under another)
                 * uploaded into ONE class: take that class's students only
                 * (LA-SW-12Oct-02). Every row is given this class further down,
                 * so the whole file used to land in it.
                 */
                /*
                 * A classes template uploaded into ONE class (Manage Students):
                 * only the rows of this class count — its Class Name, and in the
                 * network file its school. Its grade and teacher are set from the
                 * class itself further down.
                 */
                if (enrollClassRow && fileTemplate) {
                    const { blocks: ownBlocks, rows: own } = templateRowsOfClass(data, fileTemplate, enrollClassRow);
                    if (!own.length) {
                        toast(TOAST_TYPES.WARN, 'Classes', `This file has no learners for "${enrollClassRow.name}"${enrollClassRow.schoolName ? ` (${enrollClassRow.schoolName})` : ''} — fill the learner columns on rows with this class's Class Name${fileTemplate === TEMPLATE_NETWORK_CLASS ? " and its school's School Email" : ''}. Nothing was added.`);
                        return false;
                    }
                    data.splice(0, data.length, { ...ownBlocks[0], license: own });
                }
                if (enrollClassRow && !fileTemplate && stackedClasses && Array.isArray(data?.[0]?.license)) {
                    const own = licenseRowsOfClass(data[0].license, enrollClassRow.name, enrollClassRow.teacherEmail);
                    if (!own.length) {
                        toast(TOAST_TYPES.WARN, 'Classes', `This file has no students for "${enrollClassRow.name}" — it lists other classes. Nothing was added.`);
                        return false;
                    }
                    data[0].license = own;
                }
                // Rows without an Audience Type (the approved template has no such
                // column) get the school's student audience — see pickDefaultStudentUserGroup.
                let defaultUserGroup = '';
                try {
                    const infoRes = await analyticsLicensesInfoService(targetOrgCode);
                    defaultUserGroup = pickDefaultStudentUserGroup(infoRes?.data?.user_group_list);
                } catch { /* silent */ }
                if (!defaultUserGroup) {
                    try {
                        const orgModsRes = await getOrgModulesService({ org_code: targetOrgCode, page: 1, limit: 1 });
                        const firstMod = orgModsRes?.data?.modules?.[0];
                        if (firstMod?.user_group) {
                            defaultUserGroup = String(firstMod.user_group).trim();
                        }
                    } catch { /* silent */ }
                }
                if (defaultUserGroup && Array.isArray(data)) {
                    // Every block — a classes template has one per school.
                    for (const lic of data.flatMap((b) => (Array.isArray(b?.license) ? b.license : []))) {
                        if (!lic.user_group) lic.user_group = defaultUserGroup;
                    }
                }
                // Upload from one class: all rows use that class name.
                // Not for a classes template: each row names its own class.
                if (!fileTemplate && sectionInfo?.section_code && Array.isArray(data?.[0]?.license)) {
                    const targetClass = String(sectionInfo.section_code).trim();
                    if (targetClass) {
                        for (const lic of data[0].license) {
                            lic.class_name = targetClass;
                        }
                    }
                }
                /*
                 * A NEW class from a file (LA-SW-05Oct-3). The file's class block is
                 * the source; where it is empty, the details typed on "Enter
                 * manually" fill in. Without a grade the API saves the learners but
                 * creates no class record, so the class never appeared in the list —
                 * and without one code for the whole file it could not be one class.
                 */
                // Schools only: a company's employee file has no class or grade to require.
                if (!enrollClassRow && orgType === ORG_TYPE_SCHOOL && Array.isArray(data?.[0]?.license)) {
                    // A classes template carries every class's details on its rows.
                    const typed = fileTemplate ? {} : (sectionInfo?.typedClass || {});
                    const rows = data.flatMap((b) => (Array.isArray(b?.license) ? b.license : []));
                    const codeByClass = {};
                    for (const lic of rows) {
                        for (const key of ['grade_level', 'academic_year', 'incharge_first_name', 'incharge_last_name', 'incharge_email']) {
                            if (!String(lic[key] || '').trim() && typed[key]) lic[key] = typed[key];
                        }
                        if (lic.grade_level && !String(lic.school_level || '').trim()) lic.school_level = lic.grade_level;
                        const name = String(lic.class_name || '').trim();
                        if (name && !String(lic.section_code || '').trim()) {
                            codeByClass[name] = codeByClass[name] || genSectionCode();
                            lic.section_code = codeByClass[name];
                        }
                    }
                    const incomplete = rows.find((lic) => !String(lic.class_name || '').trim() || !String(lic.grade_level || '').trim());
                    if (incomplete) {
                        // Stop here: uploading would create the learners with no class.
                        toast(TOAST_TYPES.WARN, 'Classes', 'Add the class name and grade level — fill the class details at the top of the template, or type them under "Add Manually" before uploading.');
                        return false;
                    }
                }
                // Enroll mode targets one SPECIFIC, already-existing class — force every
                // row's section_code to that class's real stored code (enrollClassRow.id,
                // the same id "Edit Details" uses). Without this, parseStudentLicenseWorkbookToPostData
                // has no way to know an existing class is being targeted, so it stamps a
                // freshly random section_code on every row; the backend then creates a
                // brand-new class under that code instead of adding students to the
                // existing one, even though class_name correctly matches.
                if (enrollClassRow?.id && Array.isArray(data?.[0]?.license)) {
                    for (const lic of data[0].license) {
                        lic.section_code = enrollClassRow.id;
                    }
                }
                // The enroll-mode template no longer has a "Grade Level" column at all
                // (see buildStudentUploadTemplateWorkbook's isEnrollMode branch), so
                // grade_level/school_level are never parsed from the file — this is
                // the only source for them. Force both to the existing class's real
                // grade.
                if (enrollClassRow?.grade && Array.isArray(data?.[0]?.license)) {
                    const targetGrade = String(enrollClassRow.grade).trim();
                    if (targetGrade) {
                        for (const lic of data[0].license) {
                            lic.grade_level = targetGrade;
                            lic.school_level = targetGrade;
                        }
                    }
                }
                // Same story for the teacher — the enroll-mode template has no
                // "Teacher First/Last Name/Email" columns either, so lic.incharge_*
                // is never parsed from the file. This was the actual root cause of
                // the duplicate-class bug: without it, incharge_* stayed unset and
                // the backend read the row as belonging to a different (teacherless)
                // class. Lock it to the class's real teacher, the same way the
                // manual enroll table already does (not user-editable there either).
                if (enrollClassRow && Array.isArray(data?.[0]?.license)) {
                    const targetFirst = String(enrollClassRow.teacherFirstName || '').trim();
                    const targetLast  = String(enrollClassRow.teacherLastName || '').trim();
                    const targetEmail = String(enrollClassRow.teacherEmail || '').trim();
                    for (const lic of data[0].license) {
                        if (targetFirst) lic.incharge_first_name = targetFirst;
                        if (targetLast)  lic.incharge_last_name  = targetLast;
                        if (targetEmail) lic.incharge_email      = targetEmail;
                    }
                }
                /*
                 * The classes template's classes with no learners yet (a row with
                 * the learner columns empty) — created the way Add a Class creates
                 * an empty class, one at a time, so a name that already exists
                 * (refused, as on Add a Class) is reported and does not stop the rest.
                 */
                if (fileTemplate && !enrollClassRow && Array.isArray(emptyClasses) && emptyClasses.length) {
                    const made = [];
                    const exists = [];
                    const failed = [];
                    for (const g of emptyClasses) {
                        // The school file names no school: the school picked in the dialog (a network's) holds them.
                        const school = g.school || networkTarget;
                        for (const c of g.classes) {
                            try {
                                // eslint-disable-next-line no-await-in-loop
                                const res = await createOrgClassSectionsService(
                                    school ? school.org_code : orgCode,
                                    school ? school.org_name : orgName,
                                    [{
                                        className: c.class_name,
                                        gradeLevel: c.grade_level,
                                        academicYear: c.academic_year,
                                        teacherFirstName: c.incharge_first_name,
                                        teacherLastName: c.incharge_last_name,
                                        teacherEmail: c.incharge_email,
                                    }],
                                    school?.institute_code ? { institute_code: school.institute_code } : null,
                                );
                                if (res?.data?.success || Array.isArray(res?.data?.new_list)) made.push(c.class_name);
                                else failed.push(c.class_name);
                            } catch (err) {
                                (/already exists/i.test(String(err?.message || '')) ? exists : failed).push(c.class_name);
                            }
                        }
                    }
                    const names = (list) => list.map((n) => `"${n}"`).join(', ');
                    const parts = [];
                    if (made.length) parts.push(`${made.length} class${made.length === 1 ? '' : 'es'} with no learners added`);
                    if (exists.length) parts.push(`${names(exists)} already exist${exists.length === 1 ? 's' : ''} — not added again`);
                    if (failed.length) parts.push(`${names(failed)} could not be added`);
                    const ok = !exists.length && !failed.length;
                    if (!Array.isArray(data) || !data.length) {
                        await refreshClasses();
                        toast(ok ? TOAST_TYPES.SUCCESS : TOAST_TYPES.WARN, 'Classes', `${parts.join('; ')}.`);
                        if (ok) setIsAddDialogOpen(false);
                        return ok;
                    }
                    // The learners still go up below; say now what happened to these.
                    if (!ok) toast(TOAST_TYPES.WARN, 'Classes', `${parts.join('; ')}.`);
                }
                // ── Dispatch ─────────────────────────────────────────────────
                // Large files go to the ASYNC endpoint: one request that returns
                // a job_id immediately while the server processes rows in the
                // background. The dialog closes right away and a floating banner
                // (fed by the polling effect below) tracks progress, so the user
                // is never stuck on this screen. Small files keep the original
                // synchronous request — they finish in a couple of seconds and
                // report row errors inline.
                const postOptions = { ignore_email_verification: false, use_default_password: true };
                // Every block counts: a classes template has one per school, and the
                // background job takes them all in one request.
                const totalRows = (Array.isArray(data) ? data : [])
                    .reduce((n, b) => n + (Array.isArray(b?.license) ? b.license.length : 0), 0);
                const allErrors = [];
                let lastRes = null;
                if (totalRows > UPLOAD_BATCH_SIZE) {
                    let res;
                    try {
                        res = await analyticsPostUsersCsvAsyncService({
                            org_code: targetOrgCode,
                            org_type: orgType,
                            data,
                            options: postOptions,
                        });
                    } catch {
                        // invokeApi already toasted the server/network error
                        // (e.g. "another upload is already in progress").
                        return false;
                    }
                    if (res === false) {
                        toast(TOAST_TYPES.WARN, 'Classes', 'Upload failed (encryption). Log out, sign in again, and retry.');
                        return false;
                    }
                    const jobId = res?.data?.job_id;
                    const total = Number(res?.data?.total_rows) || totalRows;
                    if (!jobId) {
                        toast(TOAST_TYPES.WARN, 'Classes', 'Upload could not start, please retry.');
                        return false;
                    }
                    // A re-upload of a previously interrupted file resumes from the
                    // server's committed-row cursor instead of row 0.
                    const resumedFrom = Number(res?.data?.resumed_from) || 0;
                    setBgUpload({ jobId, done: resumedFrom, total });
                    toast(TOAST_TYPES.INFO, 'Classes',
                        resumedFrom > 0
                            ? `Resuming upload from student ${(resumedFrom + 1).toLocaleString()} of ${total.toLocaleString()} — you can keep working while it runs.`
                            : `Uploading ${total.toLocaleString()} students in the background — you can keep working while it runs.`);
                    setIsAddDialogOpen(false);
                    return true;
                }
                // Small upload (or unexpected multi-block shape): one request,
                // exactly as before.
                lastRes = await analyticsPostUsersCsvService({
                    org_code: targetOrgCode,
                    org_type: orgType,
                    data,
                    options: postOptions,
                });
                if (lastRes === false) {
                    toast(
                        TOAST_TYPES.WARN,
                        'Classes',
                        'Upload failed (encryption). Log out, sign in again, and retry.',
                    );
                    return false;
                }
                if (Array.isArray(lastRes?.data?.errors) && lastRes.data.errors.length) {
                    allErrors.push(...lastRes.data.errors);
                }
                // Pool assignment intentionally skipped — use "Add Licenses" to set pool explicitly.
                // Auto-assigning on CSV upload inflates the total counter incorrectly.
                patchClientLicensesFromDqpInfo(setPrimaryClient, lastRes?.data?.dqp_info);
                // Reload FIRST so the table already shows the new students when
                // the dialog closes — the user never sees an empty list.
                await refreshClasses();
                if (allErrors.length) {
                    // Show first error (strip the redundant "Sheet N " prefix) and
                    // summarise the rest as a count so the toast stays readable.
                    const first = String(allErrors[0]).replace(/^Sheet \d+ /, '');
                    const rest  = allErrors.length - 1;
                    const msg   = rest > 0 ? `${first} (+${rest} more issue${rest > 1 ? 's' : ''})` : first;
                    toast(TOAST_TYPES.WARN, 'Classes', msg);
                    // Keep the dialog open: the valid rows are saved, and the file
                    // context is still on screen to fix the reported rows.
                    return false;
                }
                toast(TOAST_TYPES.SUCCESS, 'Classes', 'Student upload completed.');
                setIsAddDialogOpen(false);
                return true;
            } catch {
                // invokeApi already toasted the network/server error.
                return false;
            } finally {
                setBusy(false); // always clear spinner, even on early return
            }
        },
        // networkSchoolKey is a pure helper, recreated each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgCode, orgType, orgName, refreshClasses, enrollClassRow, schools, bgUpload, viewerIsNetwork, networkSchools],
    );

    const handleEnrollLicenseCsvUpload = useCallback(
        async (file, sectionInfo = null) => {
            if (enrollClassRow && enrollLinkedData) {
                try {
                    const {
                        data, error, stackedClasses, importTemplate: fileTemplate,
                    } = await parseStudentLicenseWorkbookToPostData(file, undefined, { networkSchools });
                    if (error) {
                        toast(TOAST_TYPES.WARN, 'Classes', error);
                        return;
                    }
                    // An exported file of several classes: count this class's students only.
                    let fileRows;
                    if (fileTemplate) fileRows = templateRowsOfClass(data, fileTemplate, enrollClassRow).rows;
                    else if (stackedClasses) fileRows = licenseRowsOfClass(data?.[0]?.license, enrollClassRow.name, enrollClassRow.teacherEmail);
                    else fileRows = data?.[0]?.license || [];
                    const rowCount = fileRows.filter((lic) =>
                        String(lic.email_address || '').trim(),
                    ).length;
                    const licCheck = getEnrollOrgWideLicenseCheck(enrollClassRow, enrollLinkedData, rowCount);
                    if (!licCheck.ok) {
                        toast(TOAST_TYPES.WARN, 'Classes', licCheck.title);
                        return;
                    }
                } catch (err) {
                    toast(TOAST_TYPES.WARN, 'Classes', err?.message || 'Could not read CSV.');
                    return;
                }
            }
            // Adding students to an EXISTING class → force its own school so a
            // school block in the uploaded file cannot move/duplicate the class.
            const lockedSchool = enrollClassRow?.instituteCode && enrollClassRow.instituteCode !== 'default'
                ? { institute_code: enrollClassRow.instituteCode }
                : null;
            const ok = await handleLicenseCsvUpload(file, { ...(sectionInfo || {}), forceSchool: lockedSchool });
            // Close only on FULL success — on any failure the dialog stays open
            // (with the file still selected) so the user can retry/resume.
            if (ok) {
                setIsEnrollDialogOpen(false);
                setEnrollClassRow(null);
            }
        },
        [handleLicenseCsvUpload, enrollClassRow, enrollLinkedData, networkSchools],
    );

    // ── Background upload polling ────────────────────────────────────────────
    // While a server-side upload job runs, poll its status every 2.5s to feed
    // the floating progress banner. On completion the class list reloads and
    // the license header is patched; on failure the toast tells the user to
    // re-upload the same file (rows already saved are updated, not duplicated).
    useEffect(() => {
        if (!bgUpload?.jobId || !orgCode) return undefined;
        const jobId = bgUpload.jobId;
        let cancelled = false;
        let inFlight = false;
        let failedPolls = 0;
        const finish = () => {
            cancelled = true;
            clearInterval(timer);
            setBgUpload((prev) => (prev?.jobId === jobId ? null : prev));
        };
        const tick = async () => {
            if (cancelled || inFlight) return;
            inFlight = true;
            try {
                const res = await analyticsCsvUploadJobStatusService({ org_code: orgCode, job_id: jobId });
                if (cancelled) return;
                failedPolls = 0;
                const job = res === false ? null : res?.data;
                if (!job) return;
                setBgUpload((prev) => (prev?.jobId === jobId
                    ? { ...prev, done: Number(job.processed_rows) || 0, total: Number(job.total_rows) || prev.total }
                    : prev));
                if (job.status === 'processing'
                    && job.updatedAt
                    && Date.now() - new Date(job.updatedAt).getTime() > UPLOAD_JOB_STALE_MS) {
                    // No server heartbeat for a while — the API likely restarted
                    // mid-run. Stop polling; rows already saved are kept.
                    finish();
                    refreshClasses().catch(() => {});
                    toast(TOAST_TYPES.WARN, 'Classes',
                        'The upload appears to have been interrupted. Upload the same file again to continue — already-added students are updated, not duplicated.');
                    return;
                }
                const toastRowErrors = (errs) => {
                    const first = String(errs[0]).replace(/^Sheet \d+ /, '');
                    const rest = errs.length - 1;
                    toast(TOAST_TYPES.WARN, 'Classes',
                        rest > 0 ? `${first} (+${rest} more issue${rest > 1 ? 's' : ''})` : first);
                };
                if (job.status === 'completed') {
                    finish();
                    patchClientLicensesFromDqpInfo(setPrimaryClient, job.result?.dqp_info);
                    refreshClasses().catch(() => {});
                    const errs = Array.isArray(job.row_errors) ? job.row_errors : [];
                    if (errs.length) {
                        toastRowErrors(errs);
                    } else {
                        toast(TOAST_TYPES.SUCCESS, 'Classes', 'Student upload completed.');
                    }
                } else if (job.status === 'failed') {
                    finish();
                    refreshClasses().catch(() => {});
                    const msg = String(job.error_message || '').replace(/^NOK:/, '')
                        || 'Upload failed — upload the same file again to continue; already-added students are updated, not duplicated.';
                    toast(TOAST_TYPES.WARN, 'Classes', msg);
                    // Row errors collected before the failure are persisted per
                    // chunk — show them so fixable rows aren't silently lost.
                    const errs = Array.isArray(job.row_errors) ? job.row_errors : [];
                    if (errs.length) toastRowErrors(errs);
                }
            } catch {
                // Transient poll failure — the job keeps running server-side.
                failedPolls += 1;
                if (failedPolls >= 4) {
                    finish();
                    toast(TOAST_TYPES.WARN, 'Classes',
                        'Lost track of the upload progress (connection). The upload continues on the server — refresh this page in a minute to see the new students.');
                }
            } finally {
                inFlight = false;
            }
        };
        const timer = setInterval(tick, 2500);
        tick();
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgUpload?.jobId, orgCode]);

    // ── Background upload resume ─────────────────────────────────────────────
    // The banner state lives in this page, so navigating away unmounts it while
    // the job keeps running server-side. On mount, ask the API for the org's
    // active upload job (no job_id → latest processing job) and re-attach the
    // banner + polling to it.
    useEffect(() => {
        if (!orgCode) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await analyticsCsvUploadJobStatusService({ org_code: orgCode });
                if (cancelled || res === false) return;
                const job = res?.data;
                if (!job || job.status !== 'processing' || !job.job_id) return;
                // Skip stalled jobs — resuming one would just poll forever.
                if (job.updatedAt
                    && Date.now() - new Date(job.updatedAt).getTime() > UPLOAD_JOB_STALE_MS) return;
                setBgUpload((prev) => prev || {
                    jobId: job.job_id,
                    done: Number(job.processed_rows) || 0,
                    total: Number(job.total_rows) || 0,
                });
            } catch { /* silent — no banner is an acceptable fallback */ }
        })();
        return () => { cancelled = true; };
    }, [orgCode]);

    useEffect(() => {
        getInsiderAssessmentsService().then((res) => {
            setAssessments(res?.data?.assessments || []);
        });
    }, []);

    /*
     * A teacher's classes are the session's copy (workspace_memberships), taken at
     * login. A school adding or removing one of their classes changes only the
     * database, so the list stayed as it was until the teacher signed in again
     * (Lydia, 22 Oct: "I added a class to LC teacher but removed it later, it's
     * not removed from the LC teacher interface"). Re-read ONLY the memberships when
     * My Classes opens — the rest of the session stays the login one.
     */
    useEffect(() => {
        if (!isTeacher(authUser)) return;
        getUserDetailsService()
            .then((res) => {
                const fresh = (res?.data?.user || res?.data)?.workspace_memberships;
                if (!Array.isArray(fresh)) return;
                if (JSON.stringify(fresh) === JSON.stringify(authUser?.workspace_memberships || [])) return;
                setAuthUser({ ...authUser, workspace_memberships: fresh });
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Onboarding: visiting Your classes (the level-22 client's Step 2 "Accounts
    // Setup" card) marks Step 2 complete so Step 3 unlocks — same on-view
    // pattern as Steps 1 and 3. The add-class write below remains as a fallback.
    useEffect(() => {
        if (!isClientOnboardingUser(authUser)) return;
        updateUserDetailsService({ complete_onboarding_step: 2 })
            .then(() => getUserDetailsService())
            .then(res => {
                const refreshedUser = res?.data?.user || res?.data;
                // Merge ONLY the onboarding progress — replacing the whole
                // authUser drops login-time session fields (inherited
                // dq_pro_plan etc.) and 404s the classes route for level-22.
                const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                if (merged !== authUser) setAuthUser(merged);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getInsiderProgramsService(1, 100).then((res) => {
            // learning_programs also carries conversations that have not been
            // built. There is nothing to assign to a class until one is.
            const rows = res?.data?.learning_programs || [];
            setLearningPrograms(rows.filter((p) => p && p.is_completed !== false));
        });
    }, []);

    const handleAssignLicenses = async ({ org_code, license_count, section_code }) => {
        /*
         * `id` is a DISPLAY key — for a network row it is `org_code:section_code`,
         * so sending it as the section code asks the server for a class named
         * "c8d086d9-a85_dqin:SEC_8d3ead5f", which no org has. sectionCode is the
         * real one.
         */
        const sectionCode = String(
            section_code || assignSectionRow?.sectionCode || assignSectionRow?.id || '',
        ).trim();
        if (!sectionCode) {
            toast(TOAST_TYPES.WARN, 'Classes', 'No class selected for license allocation.');
            return;
        }
        setAssigning(true);
        try {
            const response = await assignSectionLicensesService(org_code, sectionCode, license_count);
            if (response === false) {
                toast(
                    TOAST_TYPES.WARN,
                    'Classes',
                    'Could not assign licenses (encryption). Log out, sign in again, and retry.',
                );
                return;
            }
            if (response?.data?.section_licenses) {
                const orgLic = response?.data?.org_licenses;
                if (orgLic) {
                    setPrimaryClient(buildOrgLicenseClient(orgLic));
                }
                toast(TOAST_TYPES.SUCCESS, 'Classes', 'Class license pool updated.');
                setAssignDialogOpen(false);
                setAssignSectionRow(null);
                await refreshClasses();
                return;
            }
            toast(TOAST_TYPES.WARN, 'Classes', 'License assignment did not complete. Please try again.');
        } catch {
            // invokeApi already showed a warning toast; no additional toast needed
        } finally {
            setAssigning(false);
        }
    };

    const handleOpenAssignLicenses = async (cls) => {
        if (!orgCode || !orgName) {
            toast(TOAST_TYPES.WARN, 'Classes', 'No client organization loaded.');
            return;
        }
        if (!cls?.id) {
            toast(TOAST_TYPES.WARN, 'Classes', 'Select a class row to assign licenses.');
            return;
        }
        const sectionCode = String(cls.id).trim();
        setAssignSectionRow(cls);
        setAssignLicenseStats({ used: cls.licenseStats?.used || 0, total: cls.licenseStats?.total || 0 });
        setAssignDialogOpen(true);
        setAssignLicenseStatsLoading(true);
        try {
            const res = await getSectionLicensesService(orgCode, sectionCode);
            if (res === false) {
                return;
            }
            const lic = res?.data?.licenses;
            if (lic && typeof lic === 'object') {
                // used = module-linking deductions (licenses.used, 0 until a module is linked)
                setAssignLicenseStats({
                    used:  Number(lic.used)  || 0,
                    total: Number(lic.total) || 0,
                });
            }
        } catch {
            /* keep row snapshot from table */
        } finally {
            setAssignLicenseStatsLoading(false);
        }
    };

    const handleOpenViewClass = async (cls) => {
        if (!orgCode || !cls?.id) return;
        const sectionCode = String(cls.id).trim();
        setViewClassRow(cls);
        setViewLinkedData(null);
        setViewLinkedLoading(true);
        try {
            const res = await getSectionLinkedModulesService(orgCode, sectionCode);
            if (res !== false && res?.data) {
                setViewLinkedData(res.data);
            }
        } catch {
            setViewLinkedData(null);
        } finally {
            setViewLinkedLoading(false);
        }
    };

    const handleCloseViewClass = () => {
        setViewClassRow(null);
        setViewLinkedData(null);
        setViewLinkedLoading(false);
    };

    const handleOpenLinkChooserForClass = (cls) => {
        if (!orgCode) return;
        setLinkSectionRow(cls);
        setLinkTypeChooserOpen(true);
    };

    const getClassDashboardUrl = (cls) => {
        const sectionCode = String(cls.id || '').trim();
        return sectionCode ? `/dq_pro/class-dashboard/executive-overview?section_code=${encodeURIComponent(sectionCode)}` : null;
    };

    const closeLinkTypeChooser = () => {
        setLinkTypeChooserOpen(false);
        setLinkSectionRow(null);
    };

    const handleChooserLearningProgram = async () => {
        if (!orgCode || !linkSectionRow) return;
        const sectionCode = String(linkSectionRow.id).trim();
        setLinkTypeChooserOpen(false);
        setLinkLearningOpen(true);
        setLinkLearningListLoading(true);
        try {
            const { rows, breakdown } = await loadClassLinkPicker(orgCode, sectionCode, 'lp', linkSectionRow.instituteCode);
            setLinkedLearningCodes(breakdown.all);
            setOrgWideLearningCodes(breakdown.orgWide);
            setSectionLinkedLearningCodes(breakdown.sectionLinked);
            setLpPickerRows(rows);
            if (rows.length) setLearningPrograms(rows);
        } catch (err) {
            setLinkedLearningCodes([]);
            setOrgWideLearningCodes([]);
            setSectionLinkedLearningCodes([]);
            setLpPickerRows([]);
            toast(TOAST_TYPES.DANGER, 'Classes', 'Could not load programs for this class.');
        } finally {
            setLinkLearningListLoading(false);
        }
    };

    const handleChooserAssessment = async () => {
        if (!orgCode || !linkSectionRow) return;
        const sectionCode = String(linkSectionRow.id).trim();
        setLinkTypeChooserOpen(false);
        setLinkAssessmentOpen(true);
        setLinkAssessmentListLoading(true);
        try {
            const { rows, breakdown } = await loadClassLinkPicker(orgCode, sectionCode, 'assessment', linkSectionRow.instituteCode);
            setLinkedAssessmentCodes(breakdown.all);
            setOrgWideAssessmentCodes(breakdown.orgWide);
            setSectionLinkedAssessmentCodes(breakdown.sectionLinked);
            setAssessmentPickerRows(rows);
            if (rows.length) setAssessments(rows);
        } catch (err) {
            setLinkedAssessmentCodes([]);
            setOrgWideAssessmentCodes([]);
            setSectionLinkedAssessmentCodes([]);
            setAssessmentPickerRows([]);
            toast(TOAST_TYPES.DANGER, 'Classes', 'Could not load assessments for this class.');
        } finally {
            setLinkAssessmentListLoading(false);
        }
    };

    useEffect(() => {
        loadClasses();
    }, [loadClasses, location.pathname]);

    const postEnrollTabs = useCallback(async (enrollTabs, school = null, orgCodeOverride = null) => {
        const targetOrgCode = orgCodeOverride || orgCode;
        const enrollV = validateManualEnrollTabsForCsvPost(enrollTabs);
        if (!enrollV.ok) {
            toast(TOAST_TYPES.WARN, 'Classes', enrollV.message);
            return false;
        }
        let defaultUserGroup = '';
        try {
            const infoRes = await analyticsLicensesInfoService(targetOrgCode);
            const groups = infoRes?.data?.user_group_list;
            // Use the first available group (not just when there's exactly 1)
            if (Array.isArray(groups) && groups.length >= 1 && groups[0]) {
                defaultUserGroup = String(groups[0]).trim();
            }
        } catch { /* silent */ }
        // Fallback: if info service failed or returned no groups, read user_group
        // from the org's first available module (getOrgModulesService is educator-accessible).
        if (!defaultUserGroup) {
            try {
                const orgModsRes = await getOrgModulesService({ org_code: targetOrgCode, page: 1, limit: 1 });
                const firstMod = orgModsRes?.data?.modules?.[0];
                if (firstMod?.user_group) {
                    defaultUserGroup = String(firstMod.user_group).trim();
                }
            } catch { /* silent */ }
        }
        const { data, error } = buildInsiderLicensePostDataFromClassTabs(enrollTabs, {
            defaultUserGroup,
            school, // head block — institute_code (+ full details when creating a new school)
        });
        if (error) {
            toast(TOAST_TYPES.WARN, 'Classes', error);
            return false;
        }
        if (!data) {
            toast(TOAST_TYPES.WARN, 'Classes', 'Add at least one row with a student or parent email.');
            return false;
        }
        let res;
        try {
            res = await analyticsPostUsersCsvService({
                org_code: targetOrgCode,
                org_type: orgType,
                data,
                options: { ignore_email_verification: false, use_default_password: true },
            });
        } catch {
            // invokeApi already showed the error toast — don't show a second one
            return false;
        }
        if (res === false) {
            toast(
                TOAST_TYPES.WARN,
                'Classes',
                'Upload failed (encryption). Log out, sign in again, and retry.',
            );
            return false;
        }
        const errs = res?.data?.errors;
        if (Array.isArray(errs) && errs.length) {
            toast(TOAST_TYPES.WARN, 'Classes', errs.slice(0, 6).join(' '));
            return false;
        }
        patchClientLicensesFromDqpInfo(setPrimaryClient, res?.data?.dqp_info);
        return true;
    }, [orgCode, orgType]);

    const assignPoolsForTabs = useCallback(async (enrollTabs, shellTabs) => {
        const licAssigns = [];
        for (const cls of enrollTabs) {
            const sc = String(cls.className || '').trim();
            const count = Number(cls.studentsCount) || cls.students.length;
            if (sc && count > 0) licAssigns.push(silentAssignSectionPool(orgCode, sc, count));
        }
        for (const cls of shellTabs) {
            const sc = String(cls.className || '').trim();
            const count = Number(cls.studentsCount) || 0;
            if (sc && count > 0) licAssigns.push(silentAssignSectionPool(orgCode, sc, count));
        }
        if (licAssigns.length) await Promise.allSettled(licAssigns);
    }, [orgCode]);

    const handleSave = async (classList, school = null, networkTargetOrgCode = null) => {
        // A network has no single "workspace" org — the class belongs to
        // whichever member school the Add a Class dialog's picker resolved.
        // The picker hands back that school's PAIR key (networkSchoolKey).
        const networkTargetKey = viewerIsNetwork ? String(networkTargetOrgCode || '').trim() : '';
        const networkTarget = viewerIsNetwork
            ? networkSchools.find((s) => networkSchoolKey(s) === networkTargetKey) || null
            : null;
        const effectiveOrgCode = viewerIsNetwork ? String(networkTarget?.org_code || '').trim() : orgCode;
        const effectiveOrgName = viewerIsNetwork ? (networkTarget?.org_name || '') : orgName;
        // Name the school, or the server files the class under the network's
        // first school — every school of a network shares its org_code.
        if (viewerIsNetwork && networkTarget?.institute_code && !school?.institute_code) {
            school = { institute_code: networkTarget.institute_code };
        }
        if (!effectiveOrgCode) {
            toast(
                TOAST_TYPES.WARN,
                'Classes',
                viewerIsNetwork
                    ? 'Choose a school for this class.'
                    : 'No client organization loaded. Create a client org first.',
            );
            return;
        }
        const v = validateClassListForCreateApi(classList);
        if (!v.ok) {
            toast(TOAST_TYPES.WARN, 'Classes', v.message);
            return;
        }
        const { shellTabs, enrollTabs } = splitClassTabsForManageClassesSave(classList);
        if (!shellTabs.length && !enrollTabs.length) {
            toast(TOAST_TYPES.WARN, 'Classes', 'Enter a class name on at least one tab.');
            return;
        }
        setBusy(true);
        try {
            // The CSV/enroll path creates a NEW school itself (get-or-create on the
            // API). After it runs, downgrade to a code-only reference so the
            // sections/post path doesn't create the same school row twice.
            let sectionSchool = school;

            // Classes that actually reached the server.
            const createdTabs = [];
            let failedTabs = 0;

            if (enrollTabs.length) {
                const enrolled = await postEnrollTabs(enrollTabs, school, effectiveOrgCode);
                if (enrolled) {
                    createdTabs.push(...enrollTabs);
                    if (school?.institute_code) sectionSchool = { institute_code: school.institute_code };
                    // Sections created via the enroll/CSV path never pass through
                    // createOrgClassSectionsService, so persist their prefs here.
                    await persistClassPrefs(enrollTabs, sectionSchool, effectiveOrgCode);
                } else {
                    // A failed enrolment no longer takes the shell classes down
                    // with it: they are separate tabs and nothing about them
                    // failed. postEnrollTabs/invokeApi has already toasted why.
                    failedTabs += enrollTabs.length;
                }
            }
            if (shellTabs.length) {
                const shellPayload = tabsToClassPayload(shellTabs);
                let res = null;
                try {
                    res = await createOrgClassSectionsService(effectiveOrgCode, effectiveOrgName, shellPayload, sectionSchool);
                } catch {
                    // invokeApi already showed the error toast
                }
                if (res?.data?.success || Array.isArray(res?.data?.new_list)) {
                    createdTabs.push(...shellTabs);
                } else {
                    failedTabs += shellTabs.length;
                }
            }

            // Nothing was created — the reason has already been toasted, and the
            // dialog stays open so the user does not lose what they typed.
            if (!createdTabs.length) return;

            // Pool assignment intentionally skipped here — use "Add Licenses" button to
            // set the class sub-pool explicitly. Auto-assigning inflates the counter.
            const savedStudents = createdTabs.some((t) => enrollTabs.includes(t));
            toast(
                TOAST_TYPES.SUCCESS,
                'Classes',
                savedStudents ? 'Class(es) and students saved.' : 'Class(es) added.',
            );
            // Keep the dialog open when part of the batch failed, so the tabs that
            // did not save are still there to correct and retry.
            if (!failedTabs) setIsAddDialogOpen(false);
            await refreshClasses();

            // Onboarding: creating a class is the completion signal for the level-22
            // client's Step 2 ("Accounts Setup") — parallels client-creation for the
            // owner in DQProClientList. Gated on p0 + level-22. Fire-and-forget.
            if (isClientOnboardingUser(authUser)) {
                updateUserDetailsService({ complete_onboarding_step: 2 })
                    .then(() => getUserDetailsService())
                    .then(res => {
                        const refreshedUser = res?.data?.user || res?.data;
                        const merged = mergeOnboardingRefreshIntoAuthUser(authUser, refreshedUser);
                        if (merged !== authUser) setAuthUser(merged);
                    })
                    .catch(() => {});
            }
        } catch {
            // invokeApi already showed the error toast — no second toast needed
        } finally {
            setBusy(false);
        }
    };

    // Persist class prefs (username prefix / postal code) on the section after
    // a save — silent, never blocks the students flow.
    const persistClassPrefs = useCallback(async (tabs, school = null, orgCodeOverride = null) => {
        const targetOrgCode = orgCodeOverride || orgCode;
        await Promise.allSettled(
            (tabs || [])
                .filter((t) => String(t.userPrefix || '').trim() || String(t.schoolPostalCode || '').trim())
                .map((t) =>
                    updateOrgClassSectionService(targetOrgCode, {
                        ...t,
                        sectionCode: enrollClassRow?.id || '',
                        // Look the section up under ITS school — without this the
                        // API would search the legacy 'default' institute and
                        // recreate the class there as a duplicate.
                        instituteCode: school?.institute_code
                            || enrollClassRow?.instituteCode
                            || '',
                    }).catch(() => {}),
                ),
        );
    }, [orgCode, enrollClassRow]);

    const handleEnrollStudents = async (classList, school = null) => {
        if (!orgCode) {
            toast(TOAST_TYPES.WARN, 'Classes', 'No client organization loaded.');
            return;
        }
        // school !== null only when the dialog's School picker changed (switch
        // to another existing school, or a brand-new school for this class).
        const { enrollTabs } = splitClassTabsForManageClassesSave(classList);
        if (!enrollTabs.length) {
            // No new learners — but the user may have edited the class details
            // (username prefix / postal code / school). Persist those instead of
            // erroring.
            const tab = classList?.[0];
            const newPrefix = String(tab?.userPrefix || '').trim();
            const newPostal = String(tab?.schoolPostalCode || '').trim();
            const detailsChanged =
                !!school ||
                (newPrefix && newPrefix !== String(enrollTarget?.usernamePrefix || '').trim()) ||
                (newPostal && newPostal !== String(enrollTarget?.schoolPostalCode || '').trim());
            if (detailsChanged) {
                setBusy(true);
                try {
                    await updateOrgClassSectionService(orgCode, {
                        ...tab,
                        sectionCode: enrollClassRow?.id || '',
                        instituteCode: enrollTarget?.instituteCode || '',
                    }, school);
                    toast(TOAST_TYPES.SUCCESS, 'Classes', 'Class details updated.');
                    setIsEnrollDialogOpen(false);
                    setEnrollClassRow(null);
                    await refreshClasses();
                } catch {
                    // invokeApi already showed the error toast
                } finally {
                    setBusy(false);
                }
                return;
            }
            toast(TOAST_TYPES.WARN, 'Classes', 'Add at least one row with a learner email.');
            return;
        }
        const licCheck = getEnrollOrgWideLicenseCheck(
            enrollClassRow,
            enrollLinkedData,
            countStudentsInTabs(classList),
        );
        if (!licCheck.ok) {
            toast(TOAST_TYPES.WARN, 'Classes', licCheck.title);
            return;
        }
        setBusy(true);
        try {
            // School switch with learners: MOVE the section first (creates the
            // new school row too when isNew) so the CSV enroll below finds the
            // class under its new school instead of duplicating it.
            if (school) {
                const moved = await updateOrgClassSectionService(orgCode, {
                    ...(classList?.[0] || {}),
                    sectionCode: enrollClassRow?.id || '',
                    instituteCode: enrollTarget?.instituteCode || '',
                }, school).catch(() => false);
                if (moved === false) return;
            }
            // Enroll under the class's (possibly new) school so student profiles
            // and the section lookup stay aligned.
            const headSchool = school?.institute_code
                ? { institute_code: school.institute_code }
                : (enrollTarget?.instituteCode && enrollTarget.instituteCode !== 'default'
                    ? { institute_code: enrollTarget.instituteCode }
                    : null);
            const enrolled = await postEnrollTabs(enrollTabs, headSchool);
            if (!enrolled) return;
            // Keep the stored class prefs current (prefix/postal may have changed).
            await persistClassPrefs(enrollTabs, headSchool);
            // Pool assignment intentionally skipped — use "Add Licenses" to set pool explicitly.
            toast(TOAST_TYPES.SUCCESS, 'Classes', 'Students added to class.');
            setIsEnrollDialogOpen(false);
            setEnrollClassRow(null);
            await refreshClasses();
        } catch {
            // invokeApi already showed the error toast — no second toast needed
        } finally {
            setBusy(false);
        }
    };

    // ── Learner space publication ───────────────────────────────────────────
    // Every number shown comes from the server and is recomputed there on
    // confirm. The client never sends a count, only the classes chosen — the
    // roster can change while this dialog is open, and the charge must follow
    // the roster, not the screenshot.
    const resolvedOrgCode = String(authUser?.org_code || '').trim();
    // A network looking at one of its schools previews only that school.
    const previewInstituteCode = viewerIsNetwork ? (selectedSchoolRow?.institute_code || null) : null;
    // Teachers belong to the workspace's org: a school's own, or the network's,
    // which its schools share.
    const teacherOrgCode = viewerIsNetwork ? resolvedOrgCode : orgCode;

    const scopeName = viewerIsNetwork
        ? (selectedSchoolRow?.org_name || authUser?.org_name || classSummary.org_name)
        : (classSummary.org_name || orgName || '');

    // "All schools in the network" lists classes of every school, which can
    // share a name — label each one with its school there.
    const schoolNameFor = viewerIsNetwork && !selectedSchoolRow
        ? (c) => (networkSchools.find((sch) => String(sch.institute_code || '') === String(c.institute_code || '')) || {}).org_name || ''
        : null;

    const loadClassSummary = useCallback(async () => {
        if (!resolvedOrgCode) return EMPTY_SUMMARY;
        setClassSummaryLoading(true);
        try {
            const res = await learnerSpacePreviewService(resolvedOrgCode, null, previewInstituteCode);
            const data = res?.data || {};
            const next = {
                classes: Array.isArray(data.classes) ? data.classes : [],
                grades: Array.isArray(data.grades) ? data.grades : [],
                licenses: data.licenses || EMPTY_SUMMARY.licenses,
                org_name: data.org_name || '',
                open_licence_requests: Array.isArray(data.open_licence_requests) ? data.open_licence_requests : [],
            };
            setClassSummary(next);
            return next;
        } catch {
            setClassSummary(EMPTY_SUMMARY);
            return EMPTY_SUMMARY;
        } finally {
            setClassSummaryLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedOrgCode, previewInstituteCode]);

    const loadLcTeachers = useCallback(async () => {
        if (isTeacher(authUser) || !teacherOrgCode) {
            setLcTeachers([]);
            return;
        }
        setLcTeachersLoading(true);
        try {
            const res = await listTeacherService({ org_code: teacherOrgCode, limit: 100 });
            const list = Array.isArray(res?.data?.teachers) ? res.data.teachers : [];
            // A removed teacher stays on the list as inactive (soft delete).
            setLcTeachers(list.filter((t) => t.is_active !== false && t.is_lc_teacher));
        } catch {
            setLcTeachers([]);
        } finally {
            setLcTeachersLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teacherOrgCode, authUser?.access_level]);

    useEffect(() => { loadLcTeachers(); }, [loadLcTeachers]);

    // A network viewing one school sees that school's teachers: those teaching
    // one of its classes, plus anyone not yet given a class.
    const scopedLcTeachers = useMemo(() => {
        if (!viewerIsNetwork || !selectedSchoolRow) return lcTeachers;
        const codes = new Set(networkClassRows.map((c) => String(c.sectionCode || c.id)));
        return lcTeachers.filter((t) => !(t.section_codes || []).length
            || (t.section_codes || []).some((c) => codes.has(String(c))));
    }, [lcTeachers, viewerIsNetwork, selectedSchoolRow, networkClassRows]);

    const lcCountBySection = useMemo(() => {
        const m = new Map();
        for (const t of lcTeachers) {
            for (const c of t.section_codes || []) m.set(String(c), (m.get(String(c)) || 0) + 1);
        }
        return m;
    }, [lcTeachers]);

    const handleOpenLcTeachers = () => {
        setLcDialogOpen(true);
        loadClassSummary();
        loadLcTeachers();
    };

    /** Toolbar "Publish Learning Space" — Who gets a learning space? */
    const handleOpenPublishLearningSpace = async () => {
        setPublishResetToken((n) => n + 1);
        setPublishSelection(null);
        setPublishShortfall(null);
        setPublishStep('scope');
        const summary = await loadClassSummary();
        // A school must set up its own parental consent before any class can
        // be published (SNW, SW Parental Consent Flow, 5 Oct 2026) — the
        // server refuses the publish otherwise, so say so before the dialog.
        const drafts = summary.classes.filter((c) => !c.is_published);
        // A school that is itself still a draft cannot have its classes published.
        if (drafts.length && drafts.every((c) => c.school_not_published)) {
            setPublishStep(null);
            toast(TOAST_TYPES.WARN, 'School not published', viewerIsNetwork
                ? 'None of these schools has been published yet. Publish a school workspace before publishing its classes.'
                : 'Publish the school workspace before publishing its classes.');
        } else if (drafts.length && drafts.every((c) => c.consent_not_set)) {
            setPublishStep(null);
            // A network's own method is only a default for its schools: each
            // school confirms its own, so pointing a network at its Home page
            // (where its consent is already set) would send it nowhere.
            toast(TOAST_TYPES.WARN, 'Parental Consent', viewerIsNetwork
                ? "None of these schools has confirmed its parental consent method yet. Each school's admin confirms it from their workspace Home before its classes can be published."
                : 'Set up parental consent before publishing classes. Go to Home or Support to set it up.');
        }
    };

    const handleClosePublishFlow = () => {
        setPublishStep(null);
        setPublishSelection(null);
        setPublishShortfall(null);
    };

    const handleContinuePublish = (selection) => {
        setPublishSelection(selection);
        setPublishStep('confirm');
    };

    const handleRequestLicences = (selection, shortfall) => {
        setPublishSelection(selection);
        setPublishShortfall({
            students_selected: selection.to_activate,
            licenses_available: classSummary.licenses.available,
            shortfall,
        });
        setPublishStep('short');
    };

    /*
     * "Request licences" (deck C7) — asks the partner for what the selection is
     * short of. Only the classes go up; the server works out each school's
     * shortfall, so a network publishing across schools asks for each one.
     */
    const [licenceRequesting, setLicenceRequesting] = useState(false);
    const handleSendLicenceRequest = async () => {
        if (!publishSelection) return;
        setLicenceRequesting(true);
        try {
            const res = await createLicenceRequestService({
                org_code: resolvedOrgCode,
                section_codes: publishSelection.section_codes,
                institute_code: previewInstituteCode,
            });
            const data = res?.data || {};
            const n = Number(data.requested_licences) || 0;
            const licences = `${n} learner licence${n === 1 ? '' : 's'}`;
            if (data.already_requested) {
                toast(TOAST_TYPES.INFO, 'Already requested',
                    `You have already asked your partner for ${licences}. We'll email you once they are assigned.`);
            } else {
                toast(TOAST_TYPES.SUCCESS, 'Request sent',
                    `Your partner will review your request for ${licences}. We'll email you once they are assigned.`);
            }
            handleClosePublishFlow();
            loadClassSummary();
        } catch {
            // invokeApi already showed the reason (e.g. enough licences now).
        } finally {
            setLicenceRequesting(false);
        }
    };

    const handleConfirmPublishLearnerSpaces = async () => {
        if (!publishSelection) return;
        setPublishLoading(true);
        try {
            const res = await publishLearnerSpacesService(resolvedOrgCode, publishSelection.section_codes);
            const data = res?.data || {};
            if (data.success) {
                setPublishResult({
                    scope_label: publishSelection.label || scopeName,
                    students_activated: data.students_activated,
                    activation_emails_sent: data.activation_emails_sent,
                    published_at: data.published_at,
                });
                setPublishStep('done');
                setPublishSelection(null);
                await Promise.all([refreshClasses(), loadClassSummary()]);
            }
        } catch (err) {
            // invokeApi surfaces the backend message (short pool, consent,
            // missing emails) as a toast; go back to the selection.
            setPublishStep('scope');
            loadClassSummary();
        } finally {
            setPublishLoading(false);
        }
    };

    const handleOpenEnrollStudents = async (cls) => {
        setEnrollClassRow(cls);
        setEnrollLinkedData(null);
        setEnrollLinkedLoading(true);
        setIsEnrollDialogOpen(true);
        if (orgCode && cls?.id) {
            try {
                const res = await getSectionLinkedModulesService(orgCode, String(cls.id).trim());
                if (res !== false && res?.data) setEnrollLinkedData(res.data);
            } catch {
                setEnrollLinkedData(null);
            } finally {
                setEnrollLinkedLoading(false);
            }
        } else {
            setEnrollLinkedLoading(false);
        }
    };

    const handleOpenEditClass = (cls) => {
        setEditClassRow(cls);
        setIsEditClassDialogOpen(true);
    };

    const handleCloseEditClass = () => {
        setIsEditClassDialogOpen(false);
        setEditClassRow(null);
    };

    // "Manage Students" from inside the Edit dialog — close it and open the
    // existing Add/Existing-Students dialog for the same class.
    const handleManageStudentsFromEdit = () => {
        const cls = editClassRow;
        handleCloseEditClass();
        if (cls) handleOpenEnrollStudents(cls);
    };

    const handleSaveClassDetails = async (details) => {
        if (!orgCode || !editClassRow) return;
        setBusy(true);
        try {
            await updateOrgClassSectionService(orgCode, {
                className: details.className,
                sectionCode: editClassRow.id,
                instituteCode: editClassRow.instituteCode || '',
                gradeLevel: details.gradeLevel,
                academicYear: details.academicYear,
                teacherFirstName: details.teacherFirstName,
                teacherLastName: details.teacherLastName,
                teacherEmail: details.teacherEmail,
                userPrefix: editClassRow.usernamePrefix || '',
                schoolPostalCode: editClassRow.schoolPostalCode || '',
            }, null);
            toast(TOAST_TYPES.SUCCESS, 'Classes', 'Class details updated.');
            handleCloseEditClass();
            await refreshClasses();
        } catch {
            // invokeApi already showed the error toast
        } finally {
            setBusy(false);
        }
    };

    const enrollTarget = useMemo(() => {
        if (!enrollClassRow) return null;
        return {
            className: enrollClassRow.name,
            sectionCode: enrollClassRow.id,
            gradeLevel: enrollClassRow.grade,
            teacherFirstName: enrollClassRow.teacherFirstName,
            teacherLastName: enrollClassRow.teacherLastName,
            teacherEmail: enrollClassRow.teacherEmail,
            teacherName: enrollClassRow.teacherName,
            studentsDisplay: enrollClassRow.students,
            licensesDisplay: enrollClassRow.licenses,
            usernamePrefix: enrollClassRow.usernamePrefix || '',
            schoolPostalCode: enrollClassRow.schoolPostalCode || '',
            instituteCode: enrollClassRow.instituteCode || '',
            // Draft classes let the Existing Students tab delete a learner
            // (nothing billed yet); published ones only allow corrections.
            lifecycleStatus: enrollClassRow.lifecycleStatus,
        };
    }, [enrollClassRow]);

    const handleLinkAssessments = async (_orgCode, selectedCodes) => {
        if (!orgCode || !linkSectionRow || !selectedCodes?.length) return;
        const sectionCode = String(linkSectionRow.id).trim();
        setLinkingAssessment(true);
        try {
            const response = await linkSectionAssessmentService(orgCode, sectionCode, selectedCodes);
            applyOrgLicensesFromLinkResponse(response, setPrimaryClient);
            if (reportSectionLinkResponse(response, 'Assessment(s) linked to this class.')) {
                setLinkAssessmentOpen(false);
                setLinkSectionRow(null);
                await refreshClasses();
            }
        } catch {
            // invokeApi already showed a warning toast; no additional toast needed
        } finally {
            setLinkingAssessment(false);
        }
    };

    const handleLinkLearningPrograms = async (_orgCode, selectedCodes) => {
        if (!orgCode || !linkSectionRow || !selectedCodes?.length) return;
        const sectionCode = String(linkSectionRow.id).trim();
        setLinkingLearning(true);
        try {
            const response = await linkSectionLearningProgramService(orgCode, sectionCode, selectedCodes);
            applyOrgLicensesFromLinkResponse(response, setPrimaryClient);
            if (reportSectionLinkResponse(response, 'Learning program(s) linked to this class.')) {
                setLinkLearningOpen(false);
                setLinkSectionRow(null);
                await refreshClasses();
            }
        } catch {
            // invokeApi already showed a warning toast; no additional toast needed
        } finally {
            setLinkingLearning(false);
        }
    };

    /**
     * Per-class "Link Program" availability.
     * Checks the class's own pool (not org-wide), so each row gets the right state.
     *   total = 0     → no pool assigned yet
     *   used >= total → pool fully consumed (licenses.used tracks link-event consumption)
     * Note: 0-student classes are allowed to link — used simply won't increment
     * until students join. The backend handles billing correctly in that case.
     */
    const getClassLinkState = (cls) => {
        // PbDQ partner and direct plans: linking a program to a class needs no
        // licences — classes are not assigned any (Ian-SW-06Oct-1). The class's
        // pool is only looked at on the legacy plans that still have one.
        if (isLinkingFreeOnPlan(authUser)) return { ok: true, title: '' };
        const total = cls.licenseStats?.total || 0;
        const used  = cls.licenseStats?.used  || 0;
        if (total === 0)
            return { ok: false, title: 'No licenses assigned to this class. Click "Add Licenses" to set up a pool.' };
        if (used >= total)
            return { ok: false, title: `All ${total} seat(s) consumed. Click "Add Licenses" to add more.` };
        return { ok: true, title: '' };
    };
    // Keep poolOk for org-level guards elsewhere (org header, etc.)
    const poolOk = !primaryClient || hasEnoughLicenses(primaryClient);
    const linkDisabledTitle =
        primaryClient && primaryClient.licenses_total === 0
            ? 'No licenses available. Assign licenses first.'
            : primaryClient
                ? 'Not enough licenses remaining for each user (same rule as Client List).'
                : '';

    return (
        <Box style={{ paddingTop: '18px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {busy ? <LoaderWithBackDrop loading /> : null}
            {/* Slides 56-62: the same guide for the School and the Network workspace. */}
            {!isTeacher(authUser) && <MyClassesGuide />}

            {/* Everything below (title through the table + pagination) sits in
                one section card, separated from the page background — matching
                the reference layout rather than sitting directly on the page.
                flex:1 so it fills the full available height regardless of how
                little content there is, instead of shrink-wrapping. */}
            <Box style={{
                backgroundColor: 'var(--brand-surface, #fff)',
                borderRadius: 20,
                padding: '28px 32px',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
            }}>
            {/* "Your network › All classes" breadcrumb removed per the SNW deck
                ("Remove"). Going back to every class is still one click away:
                the school picker's "All schools in the network" option. */}
            {/*
            {viewerIsNetwork && (
                <Typography style={{ fontSize: 13, color: 'var(--brand-text-muted, #9CA3AF)', marginBottom: 6, textAlign: 'left' }}>
                    <Box
                        component="span"
                        onClick={() => setSelectedNetworkSchool('')}
                        style={{ cursor: 'pointer', color: selectedSchoolRow ? '#FF6900' : 'inherit' }}
                    >
                        {authUser?.org_name || 'Your network'}
                    </Box>
                    {' › '}
                    {selectedSchoolRow ? selectedSchoolRow.org_name : 'All classes'}
                </Typography>
            )}
            */}
            {/* Network and school header — centred, with the SNW deck's wording, in
                the same format as the My Schools page header (DQProClientList). A
                school matches the network's format (Ian-SW-29Sep-6); a teacher keeps
                the older left-aligned "Your Classes" + school name. */}
            <Typography style={centredClassesHeader
                ? { fontSize: 35, fontWeight: 800, marginBottom: 16, textAlign: 'center', color: 'var(--brand-text, #111)' }
                : { fontSize: 26, fontWeight: 800, marginBottom: 4, textAlign: 'left', color: 'var(--brand-text, #111)' }}>
                {viewerIsNetwork
                    ? (selectedSchoolRow ? `Classes at ${selectedSchoolRow.org_name}` : 'My Classes')
                    : (centredClassesHeader ? 'My Classes' : 'Your Classes')}
            </Typography>
            {!viewerIsNetwork && centredClassesHeader ? (
                <Typography style={{ fontSize: 15, color: 'var(--brand-text-muted, #888)', marginBottom: 32, textAlign: 'center' }}>
                    Manage your classes, assign licenses, and give your teachers and learners access to your programs.
                </Typography>
            ) : viewerIsNetwork ? (
                <Typography style={{ fontSize: 15, color: 'var(--brand-text-muted, #888)', marginBottom: 32, textAlign: 'center' }}>
                    {selectedSchoolRow
                        ? `${getCountries().find((c) => c.code === selectedSchoolRow.country)?.label || selectedSchoolRow.country || '—'}, licenses come from this school's assignment`
                        : 'Manage your classes, assign licenses, and give your teachers and learners access to your programs.'}
                </Typography>
            ) : orgName ? (
                <Typography style={{ fontSize: 14, color: 'var(--brand-text-muted, #888)', marginBottom: 20, textAlign: 'left' }}>
                    {orgName}
                </Typography>
            ) : null}

            {viewerIsNetwork && (
                <Box style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Classes', value: networkStats.classes },
                        { label: 'Students', value: networkStats.students },
                        { label: 'LC Teachers', value: scopedLcTeachers.length },
                        { label: 'Programs', value: networkStats.programs },
                        { label: 'Total licenses', value: networkStats.licensesTotal },
                        // No "Used licenses" card (My Classes flow deck, slide A1: Remove).
                        { label: 'Remaining licenses', value: networkStats.licensesRemaining },
                        // No "Remaining credits" card: credits are one pool shared
                        // across the whole network, not something My Classes shows
                        // (Ian-SNW-29Sep-15).
                    ].map((s) => (
                        <Box key={s.label} style={{
                            flex: 1, minWidth: 0, padding: '16px 20px', borderRadius: 12, textAlign: 'left',
                            backgroundColor: 'var(--brand-bg, #FFFBF7)', border: '1px solid rgba(0,0,0,0.08)',
                        }}>
                            <Typography style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-text, #111)' }}>
                                {s.value.toLocaleString()}
                            </Typography>
                            <Typography style={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)' }}>
                                {s.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {!viewerIsNetwork && orgCode && (() => {
                const licTotal = Number(primaryClient?.licenses_total) || 0;
                const licUsed = Number(primaryClient?.licenses_used ?? primaryClient?.licenses_allocated) || 0;
                const licRemaining = Math.max(0, licTotal - licUsed);
                // Teacher Workspace shows Classes/Students/Programs instead of the
                // license-pool stats (those belong to the school, not the teacher).
                // Students/Programs are summed from classRows (the current page) —
                // unlike the school's list, a teacher's own class list is small and
                // scoped to their own membership, so it isn't expected to paginate
                // in practice, making a page-level sum a safe stand-in for a true
                // org-wide total (which the API doesn't expose for either stat).
                // Students/Programs are summed from classRows (the current page) for
                // BOTH branches — same accepted stand-in the teacher branch already
                // used (see above): there is no org-wide Students/Programs total
                // exposed by the API, so a page-level sum is what's available. A
                // school with more classes than fit on one page will under-count
                // until a real total endpoint exists.
                const studentsSum = classRows.reduce((sum, c) => sum + (Number(c.students) || 0), 0);
                const programsSum = classRows.reduce((sum, c) => sum + (Number(c.programCount) || 0), 0);
                const stats = isTeacher(authUser)
                    ? [
                        { label: 'Classes', value: classTotal.toLocaleString() },
                        { label: 'Students', value: studentsSum.toLocaleString() },
                        { label: 'Programs', value: programsSum.toLocaleString() },
                    ]
                    : [
                        { label: 'Classes', value: classTotal.toLocaleString() },
                        { label: 'Students', value: studentsSum.toLocaleString() },
                        { label: 'LC Teachers', value: lcTeachers.length.toLocaleString() },
                        { label: 'Programs', value: programsSum.toLocaleString() },
                        { label: 'Total licenses', value: licTotal.toLocaleString() },
                        // No "Used licenses" card (My Classes flow deck, slide A1: Remove).
                        { label: 'Remaining licenses', value: licRemaining.toLocaleString() },
                        // No "Remaining credits" card — a school has no credits of
                        // its own; it's a pool shared across the network (Ian-SW-29Sep-6).
                    ];
                // Same card for every stat, credits included — no one-off
                // treatment — each grows equally to fill the full row width.
                const statCardStyle = {
                    flex: 1,
                    minWidth: 0,
                    padding: '16px 20px',
                    borderRadius: 12,
                    textAlign: 'left',
                    backgroundColor: 'var(--brand-bg, #FFFBF7)',
                    border: '1px solid rgba(0,0,0,0.08)',
                };
                return (
                    <>
                        <Box style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                            {stats.map((s) => (
                                <Box key={s.label} style={statCardStyle}>
                                    <Typography style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-text, #111)' }}>
                                        {s.value}
                                    </Typography>
                                    <Typography style={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)' }}>
                                        {s.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                );
            })()}

            {!viewerIsNetwork && !orgCode && !busy ? (
                <Typography style={{ marginLeft: 20, marginBottom: 16, color: 'var(--brand-text-muted, #666)' }}>
                    No organization is linked to your account. Sign out and sign in again, or contact your admin.
                </Typography>
            ) : null}

            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 50,marginTop:30, flexWrap: 'wrap' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        variant="outlined"
                        value={classSearch}
                        onChange={(e) => setClassSearch(e.target.value)}
                        placeholder="Search by class, grade, or teacher…"
                        style={{ minWidth: 380, backgroundColor: 'var(--brand-surface, #fff)' }}
                    />
                    {/* SNW-ALL-My Classes-Main — "Schools can be selected from this
                        dropdown." Picking one scopes the whole page (title, stat
                        cards, table) to that school; "All schools" is the aggregate. */}
                    {viewerIsNetwork && (
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            value={selectedNetworkSchool}
                            onChange={(e) => setSelectedNetworkSchool(e.target.value)}
                            SelectProps={{ displayEmpty: true }}
                            style={{ minWidth: 220, backgroundColor: 'var(--brand-surface, #fff)' }}
                        >
                            <MenuItem value="">All schools in the network</MenuItem>
                            {networkSchools.map((s) => (
                                <MenuItem key={networkSchoolKey(s)} value={networkSchoolKey(s)}>{s.org_name}</MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Nothing to export until at least one class exists — the
                        endpoint would return an empty set and the user would get
                        an empty file, so disable it rather than let it "work".
                        Hidden for a teacher on an LC/P1B plan, same as View. */}
                    {!(isTeacher(authUser) && lcActivated) && (
                        <Tooltip
                            title={
                                !effectiveClassRows.length
                                    ? 'No classes yet — add a class to enable the download.'
                                    : (classSearchApplied ? 'Downloads only the classes matching your search.' : '')
                            }
                            arrow
                        >
                            {/* MUI sets pointer-events:none on a disabled button, so the
                                not-allowed cursor (and the tooltip) has to live on the
                                wrapper. */}
                            <span style={{ cursor: (effectiveBusy || !effectiveClassRows.length) ? 'not-allowed' : 'default', display: 'inline-flex' }}>
                                <Button
                                    sx={toolbarOutlineSx}
                                    onClick={handleDownloadExistingData}
                                    disabled={effectiveBusy || !effectiveClassRows.length}
                                >
                                    Export Class Details
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {/* Order, colours and height per Ian-SNW, SW-12Oct-1 (slide 55):
                        Export · Manage LearnCreator Teachers · Publish Learning Space · + Add a Class. */}
                    {/* Manage LearnCreator Teachers (deck A2). LearnCreator has to
                        be on the account for a teacher to have anything to sign
                        in to, so the button explains itself rather than vanishing. */}
                    {!isTeacher(authUser) && (
                        <Tooltip title={lcActivated ? '' : 'Available once LearnCreator is added to your account.'} arrow>
                            <span>
                                <Button
                                    disabled={!lcActivated}
                                    variant="outlined"
                                    onClick={handleOpenLcTeachers}
                                    sx={toolbarOutlineSx}
                                >
                                    Manage LearnCreator Teachers
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {/* Publish Learning Space (deck C1) — whole school, by grade or
                        by class. Enabled while at least one class is a draft. */}
                    {!isTeacher(authUser) && (() => {
                        const hasDraftClasses = effectiveClassRows.some((c) => isAccountDraft(c.lifecycleStatus));
                        return (
                            <Tooltip title={hasDraftClasses ? '' : 'All classes are already published.'} arrow>
                                <span>
                                    <Button
                                        disabled={!hasDraftClasses}
                                        sx={toolbarFilledSx}
                                        onClick={handleOpenPublishLearningSpace}
                                    >
                                        Publish Learning Space
                                    </Button>
                                </span>
                            </Tooltip>
                        );
                    })()}
                    {/* A teacher (level 23, LC or not) never adds classes — that
                        stays with the school (spec §6). Previously an LC/P1B
                        teacher could add their own class here, matching Link
                        Programs' lcActivated gate; that carve-out is removed.
                        NETWORK NOTE: not disabled while "All schools" is
                        selected — the dialog's own "Which school?" picker
                        (required) is what resolves the target member school,
                        defaulting to selectedNetworkSchool when one is already
                        picked here. See the file-header comment. */}
                    {!isTeacher(authUser) && (
                        <Button sx={toolbarFilledSx} onClick={() => setIsAddDialogOpen(true)}>
                            + Add a Class
                        </Button>
                    )}
                </Box>
            </Box>

            <Box className={classes.tableContent}>
                {effectiveClassRows.length > 0 && (
                    <Box className={classes.tableHeader}>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Class</Typography>
                        {viewerIsNetwork && <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>School</Typography>}
                        <Typography className={classes.headerCell}>Grade</Typography>
                        <Typography className={classes.headerCell}>Students</Typography>
                        {!hideClassLicenses && <Typography className={classes.headerCell}>Licenses</Typography>}
                        <Typography className={classes.headerCell}>Programs</Typography>
                        {!isTeacher(authUser) && <Typography className={classes.headerCell}>LC Teachers</Typography>}
                        <Typography className={classes.headerCell} style={{ textAlign: 'center', marginLeft:'120px' }}>Actions</Typography>
                    </Box>
                )}

                {effectiveClassRows.length === 0 && (viewerIsNetwork || orgCode) ? (
                    <Box style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        borderRadius: '16px',
                        border: '1px dashed rgba(241, 90, 36, 0.3)',
                        minWidth: '980px',
                    }}>
                        <GroupIcon style={{ fontSize: 56, color: '#FF6900', opacity: 0.35, marginBottom: 16 }} />
                        <Typography style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-text, #333)', marginBottom: 8 }}>
                            {classSearchApplied ? 'No classes match your search' : 'No classes yet'}
                        </Typography>
                        <Typography style={{ fontSize: 14, color: 'var(--brand-text-muted, #888)', textAlign: 'center', maxWidth: 440 }}>
                            {classSearchApplied
                                ? 'Try a different class name, grade, or school.'
                                : 'Start by adding a class. Nothing goes out until you publish your workspace.'}
                        </Typography>
                        {/* SW/SNW-ALL-My Classes-Main — "Add a class button is
                            missing" from this empty state (it only lived in the
                            toolbar above). Same handler/gating as that button;
                            only offered on the genuine empty state, not the
                            "no search results" one — a teacher never adds
                            classes either way (spec §6). */}
                        {!classSearchApplied && !isTeacher(authUser) && (
                            <Button
                                sx={{ ...toolbarFilledSx, mt: '20px' }}
                                onClick={() => setIsAddDialogOpen(true)}
                            >
                                + Add a Class
                            </Button>
                        )}
                    </Box>
                ) : null}

                {effectiveClassRows.map((cls) => {
                    const { ok: clsLinkOk, title: clsLinkTitle } = getClassLinkState(cls);
                    return (
                        <React.Fragment key={cls.rowKey || cls.id}>
                            <Box className={classes.classRow}>
                                <Box>
                                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <Typography className={classes.cellText}>{cls.name}</Typography>
                                        <AccountLifecycleBadge status={cls.lifecycleStatus} />
                                    </Box>
                                    <Typography style={{ fontSize: '12px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                        {cls.teacherName || 'No teacher assigned'}
                                    </Typography>
                                    {/* Single-school view only — a network viewer gets its
                                        own dedicated School column instead (right below),
                                        matching SNW-ALL-My Classes-Main's table layout. */}
                                    {!viewerIsNetwork && cls.schoolName && (
                                        <Typography style={{ fontSize: '11px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                            {cls.schoolName}
                                        </Typography>
                                    )}
                                </Box>
                                {viewerIsNetwork && (
                                    <Typography className={classes.cellText}>{cls.schoolName || '—'}</Typography>
                                )}
                                <Typography className={classes.cellText}>{cls.grade}</Typography>
                                <Typography className={classes.cellText}>{cls.students}</Typography>
                                {!hideClassLicenses && <Typography className={classes.cellText}>{cls.licenses}</Typography>}
                                <Typography className={classes.cellText}>{cls.programCount ?? 0}</Typography>
                                {!isTeacher(authUser) && (() => {
                                    const n = lcCountBySection.get(String(cls.sectionCode || cls.id)) || 0;
                                    return (
                                        <Typography className={classes.cellText} style={n ? undefined : { color: 'var(--brand-text-muted, #B8B2AB)' }}>
                                            {n}
                                        </Typography>
                                    );
                                })()}
                                <Box className={classes.actionGroup}>
                                    {isTeacher(authUser) ? (
                                        // A teacher on an LC/P1B plan gets Link Programs only —
                                        // no separate View button alongside it (see below).
                                        !lcActivated && (
                                            <Button variant="outlined" sx={outlineActionBtnSx} onClick={() => handleOpenEnrollStudents(cls)}>
                                                View
                                            </Button>
                                        )
                                    ) : (
                                        <>
                                            <Tooltip title={isAccountPublished(cls.lifecycleStatus) ? 'Published classes can no longer be edited.' : ''} arrow>
                                                <span>
                                                    <Button
                                                        variant="outlined"
                                                        sx={outlineActionBtnSx}
                                                        disabled={isAccountPublished(cls.lifecycleStatus)}
                                                        onClick={() => handleOpenEditClass(cls)}
                                                    >
                                                        Edit Details
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Button variant="outlined" sx={outlineActionBtnSx} onClick={() => handleOpenEnrollStudents(cls)}>
                                                Manage Students
                                            </Button>
                                        </>
                                    )}
                                    {(!isTeacher(authUser) || lcActivated) && (
                                        <Tooltip title={clsLinkTitle} arrow>
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    sx={outlineActionBtnSx}
                                                    disabled={!clsLinkOk}
                                                    onClick={() => handleOpenLinkChooserForClass(cls)}
                                                >
                                                    Link Programs
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {/* No per-row publish — learning spaces are published
                                        from the toolbar's Publish Learning Space. */}
                                    {/* SW-ALL-My Classes-AssignLicense — same assignSectionLicensesService
                                        /AssignLicensesDialog flow as the partner's org-level "Assign
                                        Licenses & Credits" button, scoped to this one class via sectionCode. */}
                                    {/* Per-class "Assign Licenses" removed for school and
                                        network — classes aren't assigned licenses
                                        (Ian-SW-29Sep-6, Ian-SNW-29Sep-15). Class Dashboard
                                        below takes its place. */}
                                    {/*
                                    {!isTeacher(authUser) && (
                                        <Button variant="outlined" sx={outlineActionBtnSx} onClick={() => handleOpenAssignLicenses(cls)}>
                                            Assign Licenses
                                        </Button>
                                    )}
                                    */}
                                    {/* Every school and network — whatever its access level (a
                                        direct-plan school is 21) — holds class_dashboard on its
                                        session; the server grants it by role. A teacher's class
                                        dashboard has no workspace yet, so the button stays off. */}
                                    {!isTeacher(authUser) && isFeatureEnabled(authUser, FEATURE_KEY.CLASS_DASHBOARD) && (
                                        <Button
                                            variant="outlined"
                                            sx={outlineActionBtnSx}
                                            component="a"
                                            href={getClassDashboardUrl(cls)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Class Dashboard
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </React.Fragment>
                    );
                })}

                {/* ── Pagination (server-side): numbered pages — only the CURRENT
                    page is highlighted, so stepping Next never lights up a second
                    control the way enabled/disabled outlined buttons did. ── */}
                {classTotal > 0 && (
                    <Box style={{ display: 'flex', alignItems: 'center',marginTop: 'auto', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Typography style={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>
                                Rows per page:
                            </Typography>
                            <TextField
                                select
                                size="small"
                                variant="outlined"
                                value={classPageSize}
                                onChange={(e) => {
                                    setClassPageSize(Number(e.target.value));
                                    setClassPage(1);
                                }}
                                style={{ width: 76, backgroundColor: 'var(--brand-surface, #fff)' }}
                            >
                                {CLASS_PAGE_SIZE_OPTIONS.map((n) => (
                                    <MenuItem key={n} value={n}>{n}</MenuItem>
                                ))}
                            </TextField>
                            <Typography style={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>
                                {`${classTotal.toLocaleString()} class${classTotal === 1 ? '' : 'es'}`}
                            </Typography>
                        </Box>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Typography style={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>
                                {`Page ${classPage} of ${classTotalPages}`}
                            </Typography>
                            {/* Neutral grey in BOTH enabled states — stepping a page
                                only fades/unfades the buttons, it never recolors the
                                one you didn't click. */}
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={busy || classPage <= 1}
                                onClick={() => setClassPage((p) => Math.max(1, p - 1))}
                                sx={paginationNavBtnSx}
                            >
                                Previous
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={busy || classPage >= classTotalPages}
                                onClick={() => setClassPage((p) => p + 1)}
                                sx={paginationNavBtnSx}
                            >
                                Next
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
            </Box>

            <Dialog
                open={Boolean(viewClassRow)}
                onClose={handleCloseViewClass}
                maxWidth="sm"
                fullWidth
                classes={{ paper: classes.viewClassDialogPaper }}
                BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
            >
                <IconButton
                    aria-label="Close"
                    onClick={handleCloseViewClass}
                    size="small"
                    className={classes.viewClassDialogClose}
                >
                    <CloseIcon />
                </IconButton>
                <DialogTitle className={classes.viewClassDialogTitle}>
                    Class details{viewClassRow?.name ? ` — ${viewClassRow.name}` : ''}
                </DialogTitle>
                <DialogContent>
                    {viewClassRow ? (
                        <Box sx={{ pt: 0.5, pb: 1 }}>
                            <Box className={classes.viewClassStatsCard}>
                                <Box className={classes.viewClassStatRow}>
                                    <Typography className={classes.viewClassStatLabel}>Class name</Typography>
                                    <Typography className={classes.viewClassStatValue}>{viewClassRow.name}</Typography>
                                </Box>
                                <Box className={classes.viewClassStatRow}>
                                    <Typography className={classes.viewClassStatLabel}>Grade level</Typography>
                                    <Typography className={classes.viewClassStatValue}>{viewClassRow.grade}</Typography>
                                </Box>
                                <Box className={classes.viewClassStatRow}>
                                    <Typography className={classes.viewClassStatLabel}># of students</Typography>
                                    <Typography className={classes.viewClassStatValue}>{viewClassRow.students}</Typography>
                                </Box>
                                {/* Hidden — classes aren't assigned licenses (see hideClassLicenses). */}
                                {!hideClassLicenses && (
                                    <Box className={classes.viewClassStatRow}>
                                        <Typography className={classes.viewClassStatLabel}>Licenses</Typography>
                                        <Typography className={classes.viewClassStatValue}>{viewClassRow.licenses}</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box className={classes.viewClassModulesCard}>
                                <Typography className={classes.viewClassModulesHeading}>
                                    Modules visible to this class
                                </Typography>
                                {viewLinkedLoading ? (
                                    <Typography className={classes.viewClassEmptyText}>
                                        Loading linked modules…
                                    </Typography>
                                ) : null}
                                {!viewLinkedLoading && viewLinkedData ? (
                                    (() => {
                                        let hasAny = false;
                                        const blocks = VIEW_LINK_BUCKETS.map(({ key, label }) => {
                                            const arr = viewLinkedData[key];
                                            if (!Array.isArray(arr) || arr.length === 0) return null;
                                            hasAny = true;
                                            return (
                                                <Box key={key}>
                                                    <Typography className={classes.viewClassModuleGroupTitle}>
                                                        {label}
                                                    </Typography>
                                                    {arr.map((m) => (
                                                        <Typography
                                                            key={m.module_code}
                                                            className={classes.viewClassModuleItem}
                                                        >
                                                            {m.module_name || m.module_code}
                                                            {' '}
                                                            <span className={classes.viewClassModuleType}>
                                                                ({formatViewModuleType(m.module_type)})
                                                            </span>
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            );
                                        });
                                        if (!hasAny) {
                                            return (
                                                <Typography className={classes.viewClassEmptyText}>
                                                    No modules linked yet. Use Link Program on this class to add one.
                                                </Typography>
                                            );
                                        }
                                        return blocks;
                                    })()
                                ) : null}
                                {!viewLinkedLoading && !viewLinkedData ? (
                                    <Typography className={classes.viewClassEmptyText}>
                                        Could not load linked modules.
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2.5, pt: 0 }}>
                    <Button className={classes.viewClassCloseBtn} onClick={handleCloseViewClass}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <AddClassStudentsDialog
                open={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                onSave={handleSave}
                saveDisabled={busy}
                onLicenseCsvUpload={handleLicenseCsvUpload}
                onDownloadLicenseTemplate={handleDownloadLicenseTemplate}
                mode="create"
                orgCode={orgCode}
                schools={schools}
                // The picker's value is the school's PAIR key (see networkSchoolKey);
                // handleSave splits it back into org_code + institute_code.
                networkSchools={viewerIsNetwork ? networkSchools.map((s) => ({ org_code: networkSchoolKey(s), org_name: s.org_name })) : []}
                defaultNetworkOrgCode={selectedNetworkSchool}
            />

            <AddClassStudentsDialog
                open={isEnrollDialogOpen}
                onClose={() => {
                    setIsEnrollDialogOpen(false);
                    setEnrollClassRow(null);
                    setEnrollLinkedData(null);
                    setEnrollLinkedLoading(false);
                }}
                onSave={handleEnrollStudents}
                saveDisabled={busy}
                onLicenseCsvUpload={handleEnrollLicenseCsvUpload}
                onDownloadLicenseTemplate={handleDownloadLicenseTemplate}
                mode="enroll"
                enrollTarget={enrollTarget}
                linkedData={enrollLinkedData}
                linkedLoading={enrollLinkedLoading}
                orgCode={orgCode}
                schools={schools}
                // Refreshes the # Students column behind the dialog. enrollClassRow
                // stays the snapshot it was opened with; the dialog subtracts its
                // own deletions from that.
                onStudentDeleted={() => refreshClasses().catch(() => {})}
            />

            <EditClassDialog
                open={isEditClassDialogOpen}
                onClose={handleCloseEditClass}
                classRow={editClassRow}
                onSave={handleSaveClassDetails}
                onManageStudents={handleManageStudentsFromEdit}
                saveDisabled={busy}
            />

            <AssignLicensesDialog
                open={assignDialogOpen}
                onClose={() => {
                    setAssignDialogOpen(false);
                    setAssignSectionRow(null);
                    setAssignLicenseStats(null);
                }}
                onSubmit={handleAssignLicenses}
                loading={assigning}
                orgName={orgName || ''}
                /*
                 * The SCHOOL that owns the class, and the class's REAL code.
                 *
                 * `id` is a display key — for a network row it is
                 * `org_code:section_code`, and sending that as the section code
                 * asks the server for a class nobody has. `orgCode` is the
                 * viewer's own org, which is right once a school shares its
                 * network's code and wrong for one that still has its own.
                 */
                orgCode={assignSectionRow?.schoolOrgCode || orgCode || ''}
                sectionName={assignSectionRow?.name}
                sectionCode={assignSectionRow?.sectionCode || assignSectionRow?.id}
                sectionLicenseStats={assignLicenseStats}
                sectionLicenseStatsLoading={assignLicenseStatsLoading}
            />

            {/* The same "What would you like to link?" pop-up as My Schools
                (Ian-PW,SNW,SW-19Oct-1). Which cards show is decided here, by
                product as before — not by plan: it used to be hidden whenever
                LearnCreator was on, which also hid COS from a school that holds it. */}
            <LinkTypePickerDialog
                open={linkTypeChooserOpen}
                onClose={closeLinkTypeChooser}
                onChoose={(type) => (type === LINK_TYPE.LEARNING_PROGRAM ? handleChooserLearningProgram() : handleChooserAssessment())}
                orgName={linkSectionRow?.name}
                cardKeys={[
                    ...(canLinkLearningProgram ? [LINK_TYPE.LEARNING_PROGRAM] : []),
                    ...(canLinkAssessment ? [LINK_TYPE.STANDARD_ASSESSMENT] : []),
                ]}
                targetNoun="class"
            />

            <ManageClassesLinkProgramDialog
                programs={lpPickerRows}
                open={linkLearningOpen}
                onSubmit={handleLinkLearningPrograms}
                loading={linkingLearning}
                onClose={() => {
                    setLinkLearningOpen(false);
                    setLinkSectionRow(null);
                }}
                orgName={orgName || ''}
                orgCode={orgCode || ''}
                linkedProgramCodes={linkedLearningCodes}
                orgWideLinkedCodes={orgWideLearningCodes}
                sectionLinkedCodes={sectionLinkedLearningCodes}
                linkingLoading={linkLearningListLoading}
                forSectionName={linkSectionRow?.name}
                forSectionCode={linkSectionRow?.id}
            />

            <ManageClassesLinkAssessmentDialog
                assessments={assessmentPickerRows}
                open={linkAssessmentOpen}
                onSubmit={handleLinkAssessments}
                loading={linkingAssessment}
                onClose={() => {
                    setLinkAssessmentOpen(false);
                    setLinkSectionRow(null);
                }}
                orgName={orgName || ''}
                orgCode={orgCode || ''}
                linkedAssessmentCodes={linkedAssessmentCodes}
                orgWideLinkedCodes={orgWideAssessmentCodes}
                sectionLinkedCodes={sectionLinkedAssessmentCodes}
                linkingLoading={linkAssessmentListLoading}
                forSectionName={linkSectionRow?.name}
                forSectionCode={linkSectionRow?.id}
            />

            {bgUpload ? (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 1400,
                        width: 340,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 6,
                        p: 2,
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                        {`Uploading students… ${bgUpload.done.toLocaleString()} / ${bgUpload.total.toLocaleString()}`}
                    </Typography>
                    <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box
                            sx={{
                                height: '100%',
                                borderRadius: 4,
                                bgcolor: 'primary.main',
                                transition: 'width 0.6s ease',
                                width: `${Math.round((bgUpload.done / Math.max(bgUpload.total, 1)) * 100)}%`,
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
                        You can keep working — the class list refreshes automatically when the upload finishes.
                    </Typography>
                </Box>
            ) : null}

            {/* ── Manage LearnCreator Teachers (deck A2-A13, B1-B3) ─────────────── */}
            <ManageLearnCreatorTeachersDialog
                open={lcDialogOpen}
                onClose={() => setLcDialogOpen(false)}
                teachers={scopedLcTeachers}
                classes={classSummary.classes}
                loading={lcTeachersLoading && !lcTeachers.length}
                orgCode={teacherOrgCode}
                schoolNameFor={schoolNameFor}
                onChanged={loadLcTeachers}
            />

            {/* ── Publish Learning Space (deck C1-C8) ──────────────────────────── */}
            <LearnerSpaceScopeDialog
                open={publishStep === 'scope'}
                onClose={handleClosePublishFlow}
                onContinue={handleContinuePublish}
                onRequestLicences={handleRequestLicences}
                loading={classSummaryLoading}
                classes={classSummary.classes}
                grades={classSummary.grades}
                licenses={classSummary.licenses}
                scopeName={scopeName}
                schoolNameFor={schoolNameFor}
                openLicenceRequests={classSummary.open_licence_requests || []}
                resetToken={publishResetToken}
            />
            <ConfirmPublishLearnerSpacesDialog
                open={publishStep === 'confirm'}
                onClose={() => setPublishStep('scope')}
                onConfirm={handleConfirmPublishLearnerSpaces}
                loading={publishLoading}
                selection={publishSelection}
                licenses={classSummary.licenses}
            />
            {/* Request licences goes to the partner's Opportunity Pipeline. This
                page only runs for a school or network (it scopes on the user's
                own org_code); the partner assigns from My Schools instead. */}
            <InsufficientLicencesDialog
                open={publishStep === 'short'}
                onClose={licenceRequesting ? undefined : handleClosePublishFlow}
                onRequest={handleSendLicenceRequest}
                requestAvailable={!!resolvedOrgCode}
                requesting={licenceRequesting}
                openLicenceRequests={classSummary.open_licence_requests || []}
                onChangeSelection={() => setPublishStep('scope')}
                studentsSelected={publishShortfall?.students_selected || 0}
                licensesAvailable={publishShortfall?.licenses_available || 0}
                shortfall={publishShortfall?.shortfall || 0}
            />
            <LearningSpacePublishedDialog
                open={publishStep === 'done'}
                onClose={() => { setPublishStep(null); setPublishResult(null); }}
                scopeLabel={publishResult?.scope_label}
                studentsActivated={publishResult?.students_activated || 0}
                emailsSent={publishResult?.activation_emails_sent || 0}
                publishedAt={publishResult?.published_at}
            />
        </Box>
    );
};

export default ManageClasses;
